import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tabs,
  Tab,
  Rating,
  Tooltip,
  Alert,
  CircularProgress,
  FormHelperText,
  Divider,
  Card,
  CardContent,
  CardHeader,
  ListItemText,
  List,
  ListItem,
  ListItemIcon,
  Collapse,
  InputAdornment,
  Stack,
  OutlinedInput,
  Autocomplete,
  Container,
  Breadcrumbs,
  Link as MuiLink,
  useTheme,
  alpha,
  Fade,
  LinearProgress,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PaymentIcon from '@mui/icons-material/Payment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NotesIcon from '@mui/icons-material/Notes';
import InfoIcon from '@mui/icons-material/Info';
import StarIcon from '@mui/icons-material/Star';
import ViewListIcon from '@mui/icons-material/ViewList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ClassIcon from '@mui/icons-material/Class';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { styled } from '@mui/material/styles';

// Styled components for Invoice-style sections  
const SectionPaper = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  position: 'relative',
  borderRadius: 16,
  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  boxShadow: `0 4px 16px ${alpha(theme.palette.grey[900], 0.1)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '@keyframes slideInUp': {
    '0%': {
      transform: 'translateY(100%)',
      opacity: 0,
    },
    '100%': {
      transform: 'translateY(0)',
      opacity: 1,
    }
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.grey[900], 0.25)}`
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  fontSize: '1.2rem',
  color: theme.palette.primary.main,
  letterSpacing: '-0.3px',
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(2),
    fontSize: 28,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    color: 'white',
    borderRadius: 16,
    padding: theme.spacing(1),
    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`
  }
}));

const ReadOnlyTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    backgroundColor: 'white',
    borderRadius: 20,
    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
    '& fieldset': { border: 'none' },
    '& input': {
      fontSize: '0.9rem',
      fontWeight: 500,
      padding: `${theme.spacing(2.5)} ${theme.spacing(3)}`,
      color: 'rgba(0, 0, 0, 0.87) !important',
      '-webkit-text-fill-color': 'rgba(0, 0, 0, 0.87) !important'
    },
    '& textarea': {
      fontSize: '0.9rem',
      fontWeight: 500,
      padding: `${theme.spacing(2.5)} ${theme.spacing(3)}`,
      color: 'rgba(0, 0, 0, 0.87) !important',
      '-webkit-text-fill-color': 'rgba(0, 0, 0, 0.87) !important'
    }
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    fontSize: '0.9rem',
    transform: 'translate(14px, 1px) scale(1)',
    '&.Mui-focused, &.MuiFormLabel-filled': {
      transform: 'translate(14px, -17px) scale(0.75)'
    }
  },
  '& .Mui-disabled': {
    color: 'rgba(0, 0, 0, 0.87) !important',
    '-webkit-text-fill-color': 'rgba(0, 0, 0, 0.87) !important'
  }
}));

function SuppliersPage({ currentCompany }) {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { canAccessPage, canEditPage, hasPermission, isManagerOrAbove, userRole, hierarchyLevel } = usePermissions();
  
  const [tabValue, setTabValue] = useState(0);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');

  // New state variables for enhanced UI
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [isEditingSupplier, setIsEditingSupplier] = useState(false);
  const [editingSupplierData, setEditingSupplierData] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Supplier form state
  const [openSupplierDialog, setOpenSupplierDialog] = useState(false);
  const [currentSupplier, setCurrentSupplier] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [supplierForm, setSupplierForm] = useState({
    name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    website: '',
    notes: '',
    payment_terms: '',
    lead_time_days: '',
    order_notes: '',
    primary_industry: '',
    rating: 0,
    category_id: '',
    nature_of_supply: '',
    products_supplied: '',
    cost: '',
    cost_range: '',
    supplier_status: '',
    first_purchase_date: ''
  });

  // Category form state
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    onSaveCallback: null
  });

  // Delete confirmation dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('');

  // Supplier selection dialog state
  const [supplierSelectionDialogOpen, setSupplierSelectionDialogOpen] = useState(false);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [selectedSupplierForCategory, setSelectedSupplierForCategory] = useState(null);

  // Category selection dialog state
  const [categorySelectionDialogOpen, setCategorySelectionDialogOpen] = useState(false);
  const [categorySearchTerm, setCategorySearchTerm] = useState('');

  // Add state for category filter dialog (separate from category selection dialog)
  const [categoryFilterDialogOpen, setCategoryFilterDialogOpen] = useState(false);
  const [categoryFilterSearchTerm, setCategoryFilterSearchTerm] = useState('');

  // State for line items in Supplier Information
  const [productsSuppliedItems, setProductsSuppliedItems] = useState([]);
  const [newProductItem, setNewProductItem] = useState('');

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedSupplier(null); // Reset selected supplier when changing tabs
  };

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      
      // Get suppliers with category name
      const { data: suppliersData, error: suppliersError } = await supabase
        .from('suppliers')
        .select(`
          *,
          supplier_categories(name)
        `)
        .eq('company_id', companyId);

      if (suppliersError) throw suppliersError;
      setSuppliers(suppliersData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setError('Failed to load suppliers. Please try again.');
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('supplier_categories')
        .select('*')
        .eq('company_id', companyId);

      if (error) throw error;
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Failed to load supplier categories. Please try again.');
    }
  };

  const fetchCompanyData = async () => {
    try {
      if (currentCompany && currentCompany.name) {
        setCompanyName(currentCompany.name);
        return;
      }

      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();

      if (error) throw error;

      if (data) {
        setCompanyName(data.name);
      }
    } catch (err) {
      console.error('Error fetching company data:', err);
      setCompanyName('Company Name');
    }
  };

  // Load data
  useEffect(() => {
    fetchSuppliers();
    fetchCategories();
    fetchCompanyData();
  }, [companyId]);

  // Reset pagination when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedCategory, sortBy, sortOrder]);

  const canEdit = hasPermission('supply_chain.suppliers.edit', 'write');

  // Permission check
  if (!hasPermission('supply_chain.suppliers.view')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AccountBoxIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Vendor Management Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Vendor Management. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  // Handle row expansion
  const toggleRowExpansion = (supplierId) => {
    setExpandedRows(prev => ({
      ...prev,
      [supplierId]: !prev[supplierId]
    }));
  };

  // Filtering, sorting and searching functions
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCategoryFilterChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      // Toggle sort order if same field is clicked
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to ascending order for a new field
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    if (tabValue === 0) {
      setSelectedCategory('all');
    }
    setSortBy('name');
    setSortOrder('asc');
    setPage(0);
  };

  // View supplier details
  const viewSupplierDetails = (supplier) => {
    setSelectedSupplier(supplier);
  };

  // Back to supplier list
  const backToSuppliersList = () => {
    setSelectedSupplier(null);
  };

  const handleCloseSupplierDetail = () => {
    setSelectedSupplier(null);
  };

  // Inline editing functions
  const startEditingSupplier = (supplier) => {
    setIsEditingSupplier(true);
    setEditingSupplierData({
      id: supplier.id,
      name: supplier.name || '',
      contact_name: supplier.contact_name || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || '',
      city: supplier.city || '',
      state: supplier.state || '',
      zip_code: supplier.zip_code || '',
      country: supplier.country || '',
      website: supplier.website || '',
      notes: supplier.notes || '',
      payment_terms: supplier.payment_terms || '',
      lead_time_days: supplier.lead_time_days || '',
      order_notes: supplier.order_notes || '',
      primary_industry: supplier.primary_industry || '',
      rating: supplier.rating || 0,
      category_id: supplier.category_id || '',
      nature_of_supply: supplier.nature_of_supply || '',
      products_supplied: supplier.products_supplied || '',
      cost: supplier.cost || '',
      cost_range: supplier.cost_range || '',
      supplier_status: supplier.supplier_status || '',
      first_purchase_date: supplier.first_purchase_date || ''
    });
    
    // Parse line items from JSON or comma-separated strings
    try {
      const productItems = supplier.products_supplied 
        ? (typeof supplier.products_supplied === 'string' && supplier.products_supplied.startsWith('[')
            ? JSON.parse(supplier.products_supplied)
            : supplier.products_supplied.split(',').map(item => item.trim()).filter(item => item))
        : [];
      setProductsSuppliedItems(productItems);
    } catch (error) {
      console.error('Error parsing line items:', error);
      setProductsSuppliedItems([]);
    }
  };

  const cancelEditingSupplier = () => {
    setIsEditingSupplier(false);
    setEditingSupplierData(null);
    setProductsSuppliedItems([]);
    setNewProductItem('');
  };

  const saveEditingSupplier = async () => {
    try {
      if (!editingSupplierData.name) {
        setError('Supplier name is required');
        return;
      }
      
      const { error } = await supabase
        .from('suppliers')
        .update({
          name: editingSupplierData.name,
          contact_name: editingSupplierData.contact_name,
          email: editingSupplierData.email,
          phone: editingSupplierData.phone,
          address: editingSupplierData.address,
          city: editingSupplierData.city,
          state: editingSupplierData.state,
          zip_code: editingSupplierData.zip_code,
          country: editingSupplierData.country,
          website: editingSupplierData.website,
          notes: editingSupplierData.notes,
          payment_terms: editingSupplierData.payment_terms,
          lead_time_days: editingSupplierData.lead_time_days ? parseInt(editingSupplierData.lead_time_days) : null,
          order_notes: editingSupplierData.order_notes,
          primary_industry: editingSupplierData.primary_industry,
          rating: editingSupplierData.rating,
          category_id: editingSupplierData.category_id || null,
          products_supplied: JSON.stringify(productsSuppliedItems),
          cost: editingSupplierData.cost,
          cost_range: editingSupplierData.cost_range,
          supplier_status: editingSupplierData.supplier_status,
          first_purchase_date: editingSupplierData.first_purchase_date || null
        })
        .eq('id', editingSupplierData.id);

      if (error) throw error;

      // Refresh the suppliers list
      await fetchSuppliers();
      
      // Update the selected supplier data
      const updatedSupplier = await supabase
        .from('suppliers')
        .select(`
          *,
          supplier_categories(name)
        `)
        .eq('id', editingSupplierData.id)
        .single();
        
      if (updatedSupplier.data) {
        setSelectedSupplier(updatedSupplier.data);
      }

      setIsEditingSupplier(false);
      setEditingSupplierData(null);
      setProductsSuppliedItems([]);
      setNewProductItem('');
    } catch (error) {
      console.error('Error updating supplier:', error);
      setError('Failed to update supplier. Please try again.');
    }
  };

  const handleEditingSupplierChange = (field, value) => {
    setEditingSupplierData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditingRatingChange = (event, newValue) => {
    setEditingSupplierData(prev => ({
      ...prev,
      rating: newValue
    }));
  };

  // Handler functions for Products Supplied line items
  const handleAddProductItem = () => {
    if (newProductItem.trim()) {
      setProductsSuppliedItems(prev => [...prev, newProductItem.trim()]);
      setNewProductItem('');
    }
  };

  const handleRemoveProductItem = (index) => {
    setProductsSuppliedItems(prev => prev.filter((_, i) => i !== index));
  };

  // Navigate to Category tab and prepare to add a category
  const goToAddCategory = () => {
    setTabValue(1);
    setTimeout(() => setOpenCategoryDialog(true), 300);
  };

  // Supplier selection dialog handlers
  const handleOpenSupplierSelectionDialog = () => {
    setSupplierSelectionDialogOpen(true);
    setSupplierSearchTerm('');
    setSelectedSupplierForCategory(null);
  };

  const handleCloseSupplierSelectionDialog = () => {
    setSupplierSelectionDialogOpen(false);
    setSupplierSearchTerm('');
    setSelectedSupplierForCategory(null);
  };

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplierForCategory(supplier);
    setSupplierSelectionDialogOpen(false);
    // Navigate to the supplier's category
    setTabValue(0);
    if (supplier.category_id) {
      setSelectedCategory(supplier.category_id);
    }
  };

  // Filter suppliers for selection dialog
  const getFilteredSuppliersForSelection = () => {
    if (!supplierSearchTerm) return suppliers;
    
    const searchLower = supplierSearchTerm.toLowerCase();
    return suppliers.filter(supplier => 
      supplier.name.toLowerCase().includes(searchLower) ||
      (supplier.contact_name && supplier.contact_name.toLowerCase().includes(searchLower)) ||
      (supplier.email && supplier.email.toLowerCase().includes(searchLower)) ||
      (supplier.primary_industry && supplier.primary_industry.toLowerCase().includes(searchLower)) ||
      (supplier.supplier_categories && supplier.supplier_categories.name.toLowerCase().includes(searchLower))
    );
  };

  // Category selection dialog handlers
  const handleOpenCategorySelectionDialog = () => {
    setCategorySelectionDialogOpen(true);
    setCategorySearchTerm('');
  };

  const handleCloseCategorySelectionDialog = () => {
    setCategorySelectionDialogOpen(false);
    setCategorySearchTerm('');
  };

  const handleCategorySelect = (category) => {
    // Check if we have a temporary handler for inline editing
    if (window.tempCategorySelectHandler) {
      window.tempCategorySelectHandler(category);
      window.tempCategorySelectHandler = null;
    } else {
      // Normal supplier form handling
      setSupplierForm({ ...supplierForm, category_id: category.id });
      setCategorySelectionDialogOpen(false);
    }
  };

  // Filter categories for selection dialog
  const getFilteredCategoriesForSelection = () => {
    if (!categorySearchTerm) return categories;
    
    const searchLower = categorySearchTerm.toLowerCase();
    return categories.filter(category => 
      category.name.toLowerCase().includes(searchLower) ||
      (category.description && category.description.toLowerCase().includes(searchLower))
    );
  };

  // Filter categories for the filter dialog (separate from category selection dialog)
  const getFilteredCategoriesForFilter = () => {
    if (!categoryFilterSearchTerm.trim()) return categories;
    
    const searchTerm = categoryFilterSearchTerm.toLowerCase();
    return categories.filter(category => 
      category.name?.toLowerCase().includes(searchTerm) ||
      (category.description && category.description.toLowerCase().includes(searchTerm))
    );
  };

  // Handle category filter selection from filter dialog
  const handleCategoryFilterSelection = (categoryId) => {
    setSelectedCategory(categoryId);
    setCategoryFilterDialogOpen(false);
    setCategoryFilterSearchTerm('');
  };

  // Get selected category name for display
  const getSelectedCategoryName = () => {
    if (!supplierForm.category_id) return '';
    const category = categories.find(c => c.id === supplierForm.category_id);
    return category ? category.name : '';
  };

  // Apply filtering, sorting, and searching to suppliers list
  const getFilteredSuppliers = () => {
    let filtered = [...suppliers];
    
    // Apply search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(supplier => 
        supplier.name.toLowerCase().includes(searchLower) ||
        (supplier.contact_name && supplier.contact_name.toLowerCase().includes(searchLower)) ||
        (supplier.email && supplier.email.toLowerCase().includes(searchLower)) ||
        (supplier.notes && supplier.notes.toLowerCase().includes(searchLower)) ||
        (supplier.supplier_categories && supplier.supplier_categories.name.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(supplier => supplier.category_id === selectedCategory);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'category':
          const categoryA = a.supplier_categories ? a.supplier_categories.name : '';
          const categoryB = b.supplier_categories ? b.supplier_categories.name : '';
          comparison = categoryA.localeCompare(categoryB);
          break;
        case 'rating':
          comparison = (a.rating || 0) - (b.rating || 0);
          break;
        case 'location':
          const locationA = [a.city, a.state, a.country].filter(Boolean).join(', ');
          const locationB = [b.city, b.state, b.country].filter(Boolean).join(', ');
          comparison = locationA.localeCompare(locationB);
          break;
        default:
          comparison = a.name.localeCompare(b.name);
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  };

  // Apply filtering, sorting, and searching to categories list
  const getFilteredCategories = () => {
    let filtered = [...categories];
    
    // Apply search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(category => 
        category.name.toLowerCase().includes(searchLower) ||
        (category.description && category.description.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply sorting (only name for categories)
    filtered.sort((a, b) => {
      const comparison = a.name.localeCompare(b.name);
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  };



  // Supplier form handlers
  const handleOpenSupplierDialog = (supplier = null) => {
    if (supplier) {
      setCurrentSupplier(supplier);
      setSupplierForm({
        name: supplier.name || '',
        contact_name: supplier.contact_name || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        address: supplier.address || '',
        city: supplier.city || '',
        state: supplier.state || '',
        zip_code: supplier.zip_code || '',
        country: supplier.country || '',
        website: supplier.website || '',
        notes: supplier.notes || '',
        payment_terms: supplier.payment_terms || '',
        lead_time_days: supplier.lead_time_days || '',
        order_notes: supplier.order_notes || '',
        primary_industry: supplier.primary_industry || '',
        rating: supplier.rating || 0,
        category_id: supplier.category_id || '',
        nature_of_supply: supplier.nature_of_supply || '',
        products_supplied: supplier.products_supplied || '',
        cost: supplier.cost || '',
        cost_range: supplier.cost_range || '',
        supplier_status: supplier.supplier_status || '',
        first_purchase_date: supplier.first_purchase_date || ''
      });
      
      // Parse line items from JSON or comma-separated strings
      try {
        const productItems = supplier.products_supplied 
          ? (typeof supplier.products_supplied === 'string' && supplier.products_supplied.startsWith('[')
              ? JSON.parse(supplier.products_supplied)
              : supplier.products_supplied.split(',').map(item => item.trim()).filter(item => item))
          : [];
        setProductsSuppliedItems(productItems);
      } catch (error) {
        console.error('Error parsing line items:', error);
        setProductsSuppliedItems([]);
      }
    } else {
      setCurrentSupplier(null);
      setSupplierForm({
        name: '',
        contact_name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        country: '',
        website: '',
        notes: '',
        payment_terms: '',
        lead_time_days: '',
        order_notes: '',
        primary_industry: '',
        rating: 0,
        category_id: '',
        nature_of_supply: '',
        products_supplied: '',
        cost: '',
        cost_range: '',
        supplier_status: '',
        first_purchase_date: ''
      });
      setProductsSuppliedItems([]);
    }
    setOpenSupplierDialog(true);
  };

  const handleCloseSupplierDialog = () => {
    setOpenSupplierDialog(false);
    setProductsSuppliedItems([]);
    setNewProductItem('');
    setActiveStep(0);
  };

  // Stepper functions
  const steps = ['Basic Info', 'Contact', 'Address', 'Supplier Info', 'Business Terms'];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSupplierChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for category_id when "Add New Category" is selected
    if (name === 'category_id' && value === 'add_new') {
      // Save the current form state
      const currentFormState = { ...supplierForm };
      
      // Open the category dialog
      handleOpenCategoryDialog();
      
      // Set a callback to return to supplier form when done
      setCategoryForm({
        name: '',
        description: '',
        onSaveCallback: (newCategoryId) => {
          setSupplierForm({
            ...currentFormState,
            category_id: newCategoryId
          });
          setOpenSupplierDialog(true);
        }
      });
      
      // Close the supplier dialog temporarily
      setOpenSupplierDialog(false);
      return;
    }
    
    setSupplierForm({ ...supplierForm, [name]: value });
  };

  const handleRatingChange = (event, newValue) => {
    setSupplierForm({ ...supplierForm, rating: newValue });
  };

  const handleSupplierSubmit = async () => {
    try {
      setError(null); // Clear previous errors

      // STRICT VALIDATION - Check all required fields
      if (!supplierForm.name || supplierForm.name.trim() === '') {
        setError('Supplier name is required and cannot be empty.');
        return;
      }

      // Check permissions before proceeding
      if (!hasPermission('supply_chain.suppliers.edit', 'write')) {
        setError('You do not have permission to create or edit suppliers.');
        return;
      }

      // Validate cost field - must be NULL or one of the allowed values
      const validCostValues = ['Acceptable Range', 'Non Competitive'];
      let costValue = supplierForm.cost;
      if (costValue === '' || costValue === null || costValue === undefined) {
        costValue = null;
      } else if (!validCostValues.includes(costValue)) {
        setError(`Cost field must be either "Acceptable Range" or "Non Competitive". Current value: "${costValue}"`);
        return;
      }

      // Validate supplier_status field - must be NULL or one of the allowed values
      const validStatusValues = ['Approved', 'Provisional'];
      let statusValue = supplierForm.supplier_status;
      if (statusValue === '' || statusValue === null || statusValue === undefined) {
        statusValue = null;
      } else if (!validStatusValues.includes(statusValue)) {
        setError(`Supplier Status must be either "Approved" or "Provisional". Current value: "${statusValue}"`);
        return;
      }

      // Validate lead_time_days if provided (must be a valid number)
      let leadTimeValue = null;
      if (supplierForm.lead_time_days && supplierForm.lead_time_days.trim() !== '') {
        const parsed = parseInt(supplierForm.lead_time_days);
        if (isNaN(parsed) || parsed < 0) {
          setError('Lead Time Days must be a valid positive number or left empty.');
          return;
        }
        leadTimeValue = parsed;
      }

      // Prepare data with proper null handling
      const supplierData = {
        name: supplierForm.name.trim(),
        contact_name: supplierForm.contact_name?.trim() || null,
        email: supplierForm.email?.trim() || null,
        phone: supplierForm.phone?.trim() || null,
        address: supplierForm.address?.trim() || null,
        city: supplierForm.city?.trim() || null,
        state: supplierForm.state?.trim() || null,
        zip_code: supplierForm.zip_code?.trim() || null,
        country: supplierForm.country?.trim() || null,
        website: supplierForm.website?.trim() || null,
        notes: supplierForm.notes?.trim() || null,
        payment_terms: supplierForm.payment_terms?.trim() || null,
        lead_time_days: leadTimeValue,
        order_notes: supplierForm.order_notes?.trim() || null,
        primary_industry: supplierForm.primary_industry?.trim() || null,
        rating: supplierForm.rating || 0,
        category_id: supplierForm.category_id || null,
        products_supplied: JSON.stringify(productsSuppliedItems),
        cost: costValue, // NULL or valid enum value
        cost_range: supplierForm.cost_range?.trim() || null,
        supplier_status: statusValue, // NULL or valid enum value
        first_purchase_date: supplierForm.first_purchase_date || null
      };
      
      if (currentSupplier) {
        // Update existing supplier
        const { error } = await supabase
          .from('suppliers')
          .update(supplierData)
          .eq('id', currentSupplier.id);

        if (error) {
          console.error('Error saving supplier:', error);
          // Display exact error message from database
          const errorMessage = error.message || 'Unknown error occurred';
          const errorDetails = error.details ? ` Details: ${error.details}` : '';
          const errorHint = error.hint ? ` Hint: ${error.hint}` : '';
          setError(`Database Error: ${errorMessage}${errorDetails}${errorHint}`);
          return;
        }
      } else {
        // Create new supplier
        const { error } = await supabase
          .from('suppliers')
          .insert({
            company_id: companyId,
            ...supplierData
          });

        if (error) {
          console.error('Error saving supplier:', error);
          // Display exact error message from database
          const errorMessage = error.message || 'Unknown error occurred';
          const errorDetails = error.details ? ` Details: ${error.details}` : '';
          const errorHint = error.hint ? ` Hint: ${error.hint}` : '';
          setError(`Database Error: ${errorMessage}${errorDetails}${errorHint}`);
          return;
        }
      }

      // Refresh suppliers list
      fetchSuppliers();
      handleCloseSupplierDialog();
      
      // If we were viewing a supplier, refresh the selected supplier data
      if (selectedSupplier) {
        const updatedSupplier = await supabase
          .from('suppliers')
          .select(`
            *,
            supplier_categories(name)
          `)
          .eq('id', selectedSupplier.id)
          .single();
          
        if (updatedSupplier.data) {
          setSelectedSupplier(updatedSupplier.data);
        }
      }
    } catch (error) {
      console.error('Error saving supplier:', error);
      // Display exact error message
      const errorMessage = error.message || 'Unknown error occurred';
      const errorDetails = error.details ? ` Details: ${error.details}` : '';
      const errorHint = error.hint ? ` Hint: ${error.hint}` : '';
      setError(`Error: ${errorMessage}${errorDetails}${errorHint}`);
    }
  };

  // Category form handlers
  const handleOpenCategoryDialog = (category = null) => {
    if (category) {
      setCurrentCategory(category);
      setCategoryForm({
        name: category.name || '',
        description: category.description || '',
        onSaveCallback: category.onSaveCallback || null
      });
    } else {
      setCurrentCategory(null);
      setCategoryForm({
        name: '',
        description: '',
        onSaveCallback: null
      });
    }
    setOpenCategoryDialog(true);
  };

  const handleCloseCategoryDialog = () => {
    setOpenCategoryDialog(false);
  };

  const handleCategoryChange = (e) => {
    const { name, value } = e.target;
    setCategoryForm({ ...categoryForm, [name]: value });
  };

  const handleCategorySubmit = async () => {
    try {
      if (!categoryForm.name) {
        setError('Category name is required');
        return;
      }

      // Check permissions before proceeding
      if (!hasPermission('supply_chain.suppliers.edit', 'write')) {
        setError('You do not have permission to create or edit categories.');
        return;
      }
      
      let savedCategoryId = null;
      
      if (currentCategory) {
        // Update existing category
        const { error } = await supabase
          .from('supplier_categories')
          .update({
            name: categoryForm.name,
            description: categoryForm.description
          })
          .eq('id', currentCategory.id);

        if (error) throw error;
        savedCategoryId = currentCategory.id;
      } else {
        // Create new category
        const { data, error } = await supabase
          .from('supplier_categories')
          .insert({
            company_id: companyId,
            name: categoryForm.name,
            description: categoryForm.description
          })
          .select();

        if (error) throw error;
        savedCategoryId = data[0].id;
      }

      // Refresh categories list
      fetchCategories();
      
      // Execute callback if it exists (for when adding category from supplier form)
      if (categoryForm.onSaveCallback && typeof categoryForm.onSaveCallback === 'function') {
        categoryForm.onSaveCallback(savedCategoryId);
      }
      
      // Reset form and close dialog
      setCategoryForm({
        name: '',
        description: '',
        onSaveCallback: null
      });
      handleCloseCategoryDialog();
    } catch (error) {
      console.error('Error saving category:', error);
      setError('Failed to save category. Please try again.');
    }
  };

  // Delete handlers
  const handleOpenDeleteDialog = (item, type) => {
    setItemToDelete(item);
    setDeleteType(type);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setItemToDelete(null);
    setDeleteType('');
  };

  const handleDeleteItem = async () => {
    try {
      // Check permissions before proceeding
      if (!hasPermission('supply_chain.suppliers.delete', 'delete')) {
        setError(`You do not have permission to delete ${deleteType}s.`);
        handleCloseDeleteDialog();
        return;
      }

      if (deleteType === 'supplier') {
        const { error } = await supabase
          .from('suppliers')
          .delete()
          .eq('id', itemToDelete.id);

        if (error) throw error;
        fetchSuppliers();
      } else if (deleteType === 'category') {
        const { error } = await supabase
          .from('supplier_categories')
          .delete()
          .eq('id', itemToDelete.id);

        if (error) throw error;
        fetchCategories();
        fetchSuppliers(); // Refresh suppliers as category might have been removed
      }
      
      handleCloseDeleteDialog();
    } catch (error) {
      console.error(`Error deleting ${deleteType}:`, error);
      setError(`Failed to delete ${deleteType}. Please try again.`);
      handleCloseDeleteDialog();
    }
  };

  // Table pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Fade in timeout={800}>
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 3 }}>
        {/* Header Section */}
        <Box sx={{ mb: 3, pt: 2 }}>
          {/* Enhanced Page Header */}
          <Paper sx={{ 
            borderRadius: 3,
            mt: 5,
            mb: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: theme.palette.grey[100],
            backdropFilter: 'blur(20px)',
            boxShadow: 'none',
            borderTop: '3px solid #9C27B0',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 6px 20px ${alpha('#9C27B0', 0.15)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2 }}>
                <Box sx={{
                  p: 2,
                  borderRadius: 3,
                  background: alpha('#9C27B0', 0.08),
                  border: `1px solid ${alpha('#9C27B0', 0.15)}`,
                }}>
                  <AccountBoxIcon sx={{ fontSize: 32, color: '#9C27B0' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" sx={{
                    fontWeight: 500,
                    color: '#9C27B0',
                    mb: 1,
                    fontSize: '2rem',
                    letterSpacing: '-0.5px'
                  }}>
                    Vendor Management
                  </Typography>
                  <Typography variant="subtitle1" sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 500,
                    fontSize: '1rem'
                  }}>
                    {companyName || 'Loading...'}
                  </Typography>
                </Box>
                {canEdit && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenSupplierDialog()}
                  sx={{ 
                    borderRadius: 2,
                    borderColor: 'grey.400',
                    color: 'primary.main',
                    borderWidth: 1,
                    fontWeight: 500,
                    px: 3,
                    py: 1.5,
                    background: 'white',
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    '&:hover': {
                      borderColor: 'grey.600',
                      backgroundColor: 'white',
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                    }
                                              }}
                        >
                          Add Supplier
                        </Button>
                      )}
              </Box>
              <Typography variant="body2" sx={{ 
                color: 'text.secondary', 
                maxWidth: 700, 
                lineHeight: 1.6,
                fontSize: '0.95rem',
                fontWeight: 500
              }}>
                Manage your supplier relationships and vendor partnerships for your supply chain operations.
              </Typography>
            </CardContent>
          </Paper>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 1.5 }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Tabs */}
        {!selectedSupplier && (
          <Box sx={{ mt: 4, mb: 3 }}>
          <Paper sx={{ 
            borderRadius: 3, 
              mb: 0, 
            overflow: 'hidden', 
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 12px 32px ${alpha(theme.palette.grey[900], 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '@keyframes slideInUp': {
              '0%': {
                transform: 'translateY(100%)',
                opacity: 0,
              },
              '100%': {
                transform: 'translateY(0)',
                opacity: 1,
              }
            },
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="supplier management tabs"
              sx={{
                  px: 2,
                  pt: 1,
                '& .MuiTab-root': {
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  minHeight: 60,
                    px: 3,
                    mx: 1,
                    borderRadius: '8px 8px 0 0',
                    transition: 'all 0.2s ease',
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0'
                }
              }}
            >
              <Tab 
                label="Vendor Management" 
                icon={<AccountBoxIcon />} 
                iconPosition="start"
                sx={{ minWidth: 150 }}
              />
              <Tab 
                label="Categories" 
                icon={<FolderIcon />} 
                iconPosition="start"
                sx={{ minWidth: 150 }}
              />
            </Tabs>
          </Paper>
          </Box>
        )}

        {/* Search and Filters */}
        {!selectedSupplier && (
          <Box sx={{ mb: 8 }}>
            <Paper 
              sx={{ 
                p: 4, 
                mb: 0, 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '@keyframes slideInUp': {
                  '0%': {
                    transform: 'translateY(100%)',
                    opacity: 0,
                  },
                  '100%': {
                    transform: 'translateY(0)',
                    opacity: 1,
                  }
                },
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}
            >
                          <Typography 
              variant="h6" 
              sx={{ 
                mb: 3, 
                fontWeight: 500,
                color: 'primary.main',
                fontSize: '1.25rem'
              }}
            >
              {tabValue === 0 ? 'Filter & Search Suppliers' : 'Filter & Search Categories'}
            </Typography>
              <Grid container spacing={3} alignItems="end">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder={tabValue === 0 ? "Search suppliers..." : "Search categories..."}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                      sx: { 
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: 'none'
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none'
                        }
                      }
                    }}
                  />
                </Grid>
                
                {tabValue === 0 && (
                  <Grid item xs={12} sm={6} md={2}>
                    <Box>
                      <Typography variant="caption" sx={{ 
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        color: 'text.secondary',
                        mb: 1,
                        ml: 1,
                        display: 'block'
                      }}>
                        Category
                      </Typography>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => setCategoryFilterDialogOpen(true)}
                        sx={{
                          height: 56,
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          justifyContent: 'flex-start',
                          px: 2,
                          py: 1.5,
                          color: selectedCategory !== 'all' ? 'text.primary' : 'text.secondary',
                          fontWeight: 500,
                          textTransform: 'none',
                          fontSize: '0.9rem',
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          }
                        }}
                      >
                        {selectedCategory !== 'all' ? 
                          categories.find(category => category.id === selectedCategory)?.name || 'All Categories' : 
                          'All Categories'
                        }
                      </Button>
                    </Box>
                  </Grid>
                )}
                
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="medium">
                    <InputLabel 
                      id="sort-by-label" 
                      sx={{ 
                        fontSize: '0.9rem', 
                        fontWeight: 500,
                        transform: 'translate(14px, 12px) scale(1)',
                        '&.Mui-focused, &.MuiFormLabel-filled': {
                          fontSize: '0.9rem',
                          transform: 'translate(14px, -20px) scale(0.75)'
                        }
                      }}
                    >
                      Sort By
                    </InputLabel>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      label="Sort By"
                      sx={{
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        minHeight: 56,
                        '& .MuiSelect-select': {
                          py: 1.75,
                          px: 2,
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          minHeight: 'auto',
                          display: 'flex',
                          alignItems: 'center'
                        },
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }}
                    >
                      <MenuItem value="name" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Name (A-Z)</MenuItem>
                      {tabValue === 0 && (
                        <>
                          <MenuItem value="category" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Category</MenuItem>
                          <MenuItem value="rating" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Rating</MenuItem>
                          <MenuItem value="location" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Location</MenuItem>
                        </>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <Button 
                    fullWidth
                    size="medium" 
                    onClick={resetFilters}
                    disabled={tabValue === 0 ? 
                      (searchTerm === '' && selectedCategory === 'all' && sortBy === 'name') : 
                      (searchTerm === '' && sortBy === 'name')
                    }
                    sx={{ 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      fontWeight: 500,
                      height: 56,
                      px: 3,
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                      }
                    }}
                  >
                    Reset
                  </Button>
                </Grid>
                
                <Grid item xs={12} md={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 56 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, textAlign: 'center' }}>
                      {tabValue === 0 ? (
                        `${getFilteredSuppliers().length} supplier${getFilteredSuppliers().length !== 1 ? 's' : ''} found`
                      ) : (
                        `${getFilteredCategories().length} categor${getFilteredCategories().length !== 1 ? 'ies' : 'y'} found`
                      )}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}

        {/* Suppliers Tab */}
        {tabValue === 0 && (
          <Box sx={{ width: '100%' }}>
            {selectedSupplier ? (
              // Detailed Supplier View
              <Box>
                <Button 
                  startIcon={<ArrowBackIcon />} 
                  onClick={backToSuppliersList}
                  variant="outlined"
                  sx={{ 
                    mb: 3,
                    borderRadius: 6,
                    textTransform: 'none',
                    fontWeight: 500,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                    }
                  }}
                >
                  Back to Suppliers
                </Button>
                
                <Paper sx={{ 
                  p: 4, 
                  mb: 4, 
                  borderRadius: 6, 
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  boxShadow: `0 4px 16px ${alpha(theme.palette.grey[900], 0.1)}`
                }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 500, color: 'primary.main', mb: 1 }}>
                        {selectedSupplier.name}
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                        {selectedSupplier.supplier_categories && (
                          <Chip 
                            label={selectedSupplier.supplier_categories.name} 
                            size="small" 
                            color="secondary" 
                            sx={{ 
                              mb: 1,
                              fontWeight: 500,
                              borderRadius: 4
                            }}
                          />
                        )}
                        {selectedSupplier.primary_industry && (
                          <Chip 
                            label={selectedSupplier.primary_industry} 
                            size="small" 
                            variant="outlined"
                            sx={{ 
                              mb: 1,
                              fontWeight: 500,
                              borderRadius: 4
                            }}
                          />
                        )}
                        <Box display="flex" alignItems="center" sx={{ mb: 1, ml: 1 }}>
                          <Typography variant="body2" component="span" sx={{ mr: 1, fontWeight: 500 }}>Rating:</Typography>
                          <Rating value={selectedSupplier.rating || 0} readOnly />
                        </Box>
                      </Box>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      {isEditingSupplier ? (
                        <>
                          <Button
                            variant="outlined"
                            onClick={cancelEditingSupplier}
                            sx={{ 
                              borderRadius: 6,
                              textTransform: 'none',
                              fontWeight: 500,
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                              }
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={saveEditingSupplier}
                            startIcon={<EditIcon />}
                            sx={{ 
                              borderRadius: 6,
                              textTransform: 'none',
                              fontWeight: 500,
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                              boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                              '&:hover': {
                                transform: 'translateY(-1px) scale(1.02)',
                                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                              }
                            }}
                          >
                            Save Changes
                          </Button>
                        </>
                      ) : (
                        <>
                          {canEdit && (
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => startEditingSupplier(selectedSupplier)}
                              startIcon={<EditIcon />}
                            sx={{ 
                              borderRadius: 6,
                              textTransform: 'none',
                              fontWeight: 500,
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                              boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                              '&:hover': {
                                transform: 'translateY(-1px) scale(1.02)',
                                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                              }
                              }}
                            >
                              Edit
                            </Button>
                          )}
                          {canEdit && (
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => handleOpenDeleteDialog(selectedSupplier, 'supplier')}
                              startIcon={<DeleteIcon />}
                            sx={{ 
                              borderRadius: 6,
                              textTransform: 'none',
                              fontWeight: 500,
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.2)}`
                              }
                              }}
                            >
                              Delete
                            </Button>
                          )}
                        </>
                      )}
                    </Stack>
                  </Box>
                </Paper>
                
                <Grid container spacing={4}>
                  {/* Contact Information Section */}
                  <Grid item xs={12} md={6}>
                    <SectionPaper sx={{ height: '100%' }}>
                      <SectionTitle variant="h6">
                        <BusinessIcon />
                        Contact Information
                      </SectionTitle>

                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          {isEditingSupplier ? (
                            <TextField
                              label="Company Name"
                              value={editingSupplierData.name || ''}
                              onChange={(e) => handleEditingSupplierChange('name', e.target.value)}
                              fullWidth
                              variant="outlined"
                              required
                              InputProps={{
                                sx: { 
                                  borderRadius: '20px !important',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: 'white',
                                  minHeight: 56,
                                  '& input': {
                                    py: 2.5,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    minHeight: 'auto'
                                  },
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  },
                                  '&.Mui-focused': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                  }
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    border: 'none'
                                  },
                                  '&:hover fieldset': {
                                    border: 'none'
                                  },
                                  '&.Mui-focused fieldset': {
                                    border: 'none'
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  fontWeight: 500,
                                  transform: 'translate(14px, 1px) scale(1)',
                                  '&.Mui-focused, &.MuiFormLabel-filled': {
                                    transform: 'translate(14px, -17px) scale(0.75)'
                                  }
                                }
                              }}
                            />
                          ) : (
                            <ReadOnlyTextField
                              label="Company Name"
                              value={selectedSupplier.name || ''}
                              fullWidth
                              variant="outlined"
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                        </Grid>
                        
                        <Grid item xs={12}>
                          {isEditingSupplier ? (
                            <TextField
                              label="Contact Person"
                              value={editingSupplierData.contact_name || ''}
                              onChange={(e) => handleEditingSupplierChange('contact_name', e.target.value)}
                              fullWidth
                              variant="outlined"
                              InputProps={{
                                sx: { 
                                  borderRadius: '20px !important',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: 'white',
                                  minHeight: 56,
                                  '& input': {
                                    py: 2.5,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    minHeight: 'auto'
                                  },
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  },
                                  '&.Mui-focused': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                  }
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    border: 'none'
                                  },
                                  '&:hover fieldset': {
                                    border: 'none'
                                  },
                                  '&.Mui-focused fieldset': {
                                    border: 'none'
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  fontWeight: 500,
                                  transform: 'translate(14px, 1px) scale(1)',
                                  '&.Mui-focused, &.MuiFormLabel-filled': {
                                    transform: 'translate(14px, -17px) scale(0.75)'
                                  }
                                }
                              }}
                            />
                          ) : (
                            <ReadOnlyTextField
                              label="Contact Person"
                              value={selectedSupplier.contact_name || ''}
                              fullWidth
                              variant="outlined"
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          {isEditingSupplier ? (
                            <TextField
                              label="Email"
                              value={editingSupplierData.email || ''}
                              onChange={(e) => handleEditingSupplierChange('email', e.target.value)}
                              fullWidth
                              variant="outlined"
                              type="email"
                              InputProps={{
                                sx: { 
                                  borderRadius: '20px !important',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: 'white',
                                  minHeight: 56,
                                  '& input': {
                                    py: 2.5,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    minHeight: 'auto'
                                  },
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  },
                                  '&.Mui-focused': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                  }
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    border: 'none'
                                  },
                                  '&:hover fieldset': {
                                    border: 'none'
                                  },
                                  '&.Mui-focused fieldset': {
                                    border: 'none'
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  fontWeight: 500,
                                  transform: 'translate(14px, 1px) scale(1)',
                                  '&.Mui-focused, &.MuiFormLabel-filled': {
                                    transform: 'translate(14px, -17px) scale(0.75)'
                                  }
                                }
                              }}
                            />
                          ) : (
                            <ReadOnlyTextField
                              label="Email"
                              value={selectedSupplier.email || ''}
                              fullWidth
                              variant="outlined"
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          {isEditingSupplier ? (
                            <TextField
                              label="Phone"
                              value={editingSupplierData.phone || ''}
                              onChange={(e) => handleEditingSupplierChange('phone', e.target.value)}
                              fullWidth
                              variant="outlined"
                              InputProps={{
                                sx: { 
                                  borderRadius: '20px !important',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: 'white',
                                  minHeight: 56,
                                  '& input': {
                                    py: 2.5,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    minHeight: 'auto'
                                  },
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  },
                                  '&.Mui-focused': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                  }
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    border: 'none'
                                  },
                                  '&:hover fieldset': {
                                    border: 'none'
                                  },
                                  '&.Mui-focused fieldset': {
                                    border: 'none'
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  fontWeight: 500,
                                  transform: 'translate(14px, 1px) scale(1)',
                                  '&.Mui-focused, &.MuiFormLabel-filled': {
                                    transform: 'translate(14px, -17px) scale(0.75)'
                                  }
                                }
                              }}
                            />
                          ) : (
                            <ReadOnlyTextField
                              label="Phone"
                              value={selectedSupplier.phone || ''}
                              fullWidth
                              variant="outlined"
                              disabled
                            />
                          )}
                        </Grid>

                        <Grid item xs={12}>
                          {isEditingSupplier ? (
                            <TextField
                              label="Website"
                              value={editingSupplierData.website || ''}
                              onChange={(e) => handleEditingSupplierChange('website', e.target.value)}
                              fullWidth
                              variant="outlined"
                              InputProps={{
                                sx: { 
                                  borderRadius: '20px !important',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: 'white',
                                  minHeight: 56,
                                  '& input': {
                                    py: 2.5,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    minHeight: 'auto'
                                  },
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  },
                                  '&.Mui-focused': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                  }
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    border: 'none'
                                  },
                                  '&:hover fieldset': {
                                    border: 'none'
                                  },
                                  '&.Mui-focused fieldset': {
                                    border: 'none'
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  fontWeight: 500,
                                  transform: 'translate(14px, 1px) scale(1)',
                                  '&.Mui-focused, &.MuiFormLabel-filled': {
                                    transform: 'translate(14px, -17px) scale(0.75)'
                                  }
                                }
                              }}
                            />
                          ) : (
                            <ReadOnlyTextField
                              label="Website"
                              value={selectedSupplier.website || ''}
                              fullWidth
                              variant="outlined"
                              disabled
                            />
                          )}
                        </Grid>
                      </Grid>
                    </SectionPaper>
                  </Grid>

                  {/* Address Section */}
                  <Grid item xs={12} md={6}>
                    <SectionPaper sx={{ height: '100%' }}>
                      <SectionTitle variant="h6">
                        <LocationOnIcon />
                        Address
                      </SectionTitle>

                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          {isEditingSupplier ? (
                            <TextField
                              label="Street Address"
                              value={editingSupplierData.address || ''}
                              onChange={(e) => handleEditingSupplierChange('address', e.target.value)}
                              fullWidth
                              variant="outlined"
                              rows={1}
                              InputProps={{
                                sx: { 
                                  borderRadius: '20px !important',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: 'white',
                                  '& textarea': {
                                    py: 2.5,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 500
                                  },
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  },
                                  '&.Mui-focused': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                  }
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    border: 'none'
                                  },
                                  '&:hover fieldset': {
                                    border: 'none'
                                  },
                                  '&.Mui-focused fieldset': {
                                    border: 'none'
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  fontWeight: 500,
                                  transform: 'translate(14px, 1px) scale(1)',
                                  '&.Mui-focused, &.MuiFormLabel-filled': {
                                    transform: 'translate(14px, -17px) scale(0.75)'
                                  }
                                }
                              }}
                            />
                          ) : (
                            <ReadOnlyTextField
                              label="Street Address"
                              value={selectedSupplier.address || ''}
                              fullWidth
                              variant="outlined"
                              disabled                              
                              rows={1}
                            />
                          )}
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          {isEditingSupplier ? (
                            <TextField
                              label="City"
                              value={editingSupplierData.city || ''}
                              onChange={(e) => handleEditingSupplierChange('city', e.target.value)}
                              fullWidth
                              variant="outlined"
                              InputProps={{
                                sx: { 
                                  borderRadius: '20px !important',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: 'white',
                                  minHeight: 56,
                                  '& input': {
                                    py: 2.5,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    minHeight: 'auto'
                                  },
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  },
                                  '&.Mui-focused': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                  }
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    border: 'none'
                                  },
                                  '&:hover fieldset': {
                                    border: 'none'
                                  },
                                  '&.Mui-focused fieldset': {
                                    border: 'none'
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  fontWeight: 500,
                                  transform: 'translate(14px, 1px) scale(1)',
                                  '&.Mui-focused, &.MuiFormLabel-filled': {
                                    transform: 'translate(14px, -17px) scale(0.75)'
                                  }
                                }
                              }}
                            />
                          ) : (
                            <ReadOnlyTextField
                              label="City"
                              value={selectedSupplier.city || ''}
                              fullWidth
                              variant="outlined"
                              disabled
                            />
                          )}
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          {isEditingSupplier ? (
                            <TextField
                              label="State/Province"
                              value={editingSupplierData.state || ''}
                              onChange={(e) => handleEditingSupplierChange('state', e.target.value)}
                              fullWidth
                              variant="outlined"
                              InputProps={{
                                sx: { 
                                  borderRadius: '20px !important',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: 'white',
                                  minHeight: 56,
                                  '& input': {
                                    py: 2.5,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    minHeight: 'auto'
                                  },
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  },
                                  '&.Mui-focused': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                  }
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    border: 'none'
                                  },
                                  '&:hover fieldset': {
                                    border: 'none'
                                  },
                                  '&.Mui-focused fieldset': {
                                    border: 'none'
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  fontWeight: 500,
                                  transform: 'translate(14px, 1px) scale(1)',
                                  '&.Mui-focused, &.MuiFormLabel-filled': {
                                    transform: 'translate(14px, -17px) scale(0.75)'
                                  }
                                }
                              }}
                            />
                          ) : (
                            <ReadOnlyTextField
                              label="State/Province"
                              value={selectedSupplier.state || ''}
                              fullWidth
                              variant="outlined"
                              disabled
                            />
                          )}
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          {isEditingSupplier ? (
                            <TextField
                              label="ZIP/Postal Code"
                              value={editingSupplierData.zip_code || ''}
                              onChange={(e) => handleEditingSupplierChange('zip_code', e.target.value)}
                              fullWidth
                              variant="outlined"
                              InputProps={{
                                sx: { 
                                  borderRadius: '20px !important',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: 'white',
                                  minHeight: 56,
                                  '& input': {
                                    py: 2.5,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    minHeight: 'auto'
                                  },
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  },
                                  '&.Mui-focused': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                  }
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    border: 'none'
                                  },
                                  '&:hover fieldset': {
                                    border: 'none'
                                  },
                                  '&.Mui-focused fieldset': {
                                    border: 'none'
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  fontWeight: 500,
                                  transform: 'translate(14px, 1px) scale(1)',
                                  '&.Mui-focused, &.MuiFormLabel-filled': {
                                    transform: 'translate(14px, -17px) scale(0.75)'
                                  }
                                }
                              }}
                            />
                          ) : (
                            <ReadOnlyTextField
                              label="ZIP/Postal Code"
                              value={selectedSupplier.zip_code || ''}
                              fullWidth
                              variant="outlined"
                              disabled
                            />
                          )}
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          {isEditingSupplier ? (
                            <TextField
                              label="Country"
                              value={editingSupplierData.country || ''}
                              onChange={(e) => handleEditingSupplierChange('country', e.target.value)}
                              fullWidth
                              variant="outlined"
                              InputProps={{
                                sx: { 
                                  borderRadius: '20px !important',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: 'white',
                                  minHeight: 56,
                                  '& input': {
                                    py: 2.5,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    minHeight: 'auto'
                                  },
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  },
                                  '&.Mui-focused': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                  }
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    border: 'none'
                                  },
                                  '&:hover fieldset': {
                                    border: 'none'
                                  },
                                  '&.Mui-focused fieldset': {
                                    border: 'none'
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  fontWeight: 500,
                                  transform: 'translate(14px, 1px) scale(1)',
                                  '&.Mui-focused, &.MuiFormLabel-filled': {
                                    transform: 'translate(14px, -17px) scale(0.75)'
                                  }
                                }
                              }}
                            />
                          ) : (
                            <ReadOnlyTextField
                              label="Country"
                              value={selectedSupplier.country || ''}
                              fullWidth
                              variant="outlined"
                              disabled
                            />
                          )}
                        </Grid>
                      </Grid>
                    </SectionPaper>
                  </Grid>
                  
                  {/* Supplier Information Section */}
                  <Grid item xs={12}>
                    <SectionPaper>
                      <SectionTitle variant="h6">
                        <InfoIcon />
                        Supplier Information
                      </SectionTitle>

                      <Grid container spacing={3}>
                        {/* Row 1: Supplier Status, Cost */}
                        <Grid item xs={12} sm={6}>
                          {isEditingSupplier ? (
                            <FormControl fullWidth>
                              <InputLabel sx={{ 
                                fontWeight: 500,
                                transform: 'translate(14px, 1px) scale(1)',
                                '&.Mui-focused, &.MuiFormLabel-filled': {
                                  transform: 'translate(14px, -17px) scale(0.75)'
                                }
                              }}>
                                Supplier Status
                              </InputLabel>
                              <Select
                                value={editingSupplierData.supplier_status || ''}
                                onChange={(e) => handleEditingSupplierChange('supplier_status', e.target.value)}
                                sx={{
                                  borderRadius: '20px !important',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: 'white',
                                  minHeight: 56,
                                  '& .MuiSelect-select': {
                                    py: 2.5,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 500
                                  },
                                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  }
                                }}
                              >
                                <MenuItem value="">None</MenuItem>
                                <MenuItem value="Approved">Approved</MenuItem>
                                <MenuItem value="Provisional">Provisional</MenuItem>
                              </Select>
                            </FormControl>
                          ) : (
                            <ReadOnlyTextField
                              label="Supplier Status"
                              value={selectedSupplier.supplier_status || ''}
                              fullWidth
                              variant="outlined"
                              disabled
                            />
                          )}
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          {isEditingSupplier ? (
                            <FormControl fullWidth>
                              <InputLabel sx={{ 
                                fontWeight: 500,
                                transform: 'translate(14px, 1px) scale(1)',
                                '&.Mui-focused, &.MuiFormLabel-filled': {
                                  transform: 'translate(14px, -17px) scale(0.75)'
                                }
                              }}>
                                Cost
                              </InputLabel>
                              <Select
                                value={editingSupplierData.cost || ''}
                                onChange={(e) => handleEditingSupplierChange('cost', e.target.value)}
                                sx={{
                                  borderRadius: '20px !important',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: 'white',
                                  minHeight: 56,
                                  '& .MuiSelect-select': {
                                    py: 2.5,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 500
                                  },
                                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  }
                                }}
                              >
                                <MenuItem value="">None</MenuItem>
                                <MenuItem value="Acceptable Range">Acceptable Range</MenuItem>
                                <MenuItem value="Non Competitive">Non Competitive</MenuItem>
                              </Select>
                            </FormControl>
                          ) : (
                            <ReadOnlyTextField
                              label="Cost"
                              value={selectedSupplier.cost || ''}
                              fullWidth
                              variant="outlined"
                              disabled
                            />
                          )}
                        </Grid>

                        {/* Row 2: Cost Range, First Purchase Date */}
                        <Grid item xs={12} sm={6}>
                          {isEditingSupplier ? (
                            <TextField
                              label="Cost Range"
                              value={editingSupplierData.cost_range || ''}
                              onChange={(e) => handleEditingSupplierChange('cost_range', e.target.value)}
                              fullWidth
                              variant="outlined"
                              placeholder="e.g., $100-$500"
                              InputProps={{
                                sx: { 
                                  borderRadius: '20px !important',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: 'white',
                                  minHeight: 56,
                                  '& input': {
                                    py: 2.5,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    minHeight: 'auto'
                                  },
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  },
                                  '&.Mui-focused': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                  }
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    border: 'none'
                                  },
                                  '&:hover fieldset': {
                                    border: 'none'
                                  },
                                  '&.Mui-focused fieldset': {
                                    border: 'none'
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  fontWeight: 500,
                                  transform: 'translate(14px, 1px) scale(1)',
                                  '&.Mui-focused, &.MuiFormLabel-filled': {
                                    transform: 'translate(14px, -17px) scale(0.75)'
                                  }
                                }
                              }}
                            />
                          ) : (
                            <ReadOnlyTextField
                              label="Cost Range"
                              value={selectedSupplier.cost_range || ''}
                              fullWidth
                              variant="outlined"
                              disabled
                            />
                          )}
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          {isEditingSupplier ? (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                label="First Purchase Date"
                                value={editingSupplierData.first_purchase_date ? dayjs(editingSupplierData.first_purchase_date) : null}
                                onChange={(newValue) => {
                                  handleEditingSupplierChange('first_purchase_date', newValue ? newValue.format('YYYY-MM-DD') : '');
                                }}
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    variant: 'outlined',
                                    InputProps: {
                                      sx: { 
                                        borderRadius: '20px !important',
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                        background: 'white',
                                        minHeight: 56,
                                        '& input': {
                                          py: 2.5,
                                          px: 3,
                                          fontSize: '0.9rem',
                                          fontWeight: 500,
                                          minHeight: 'auto'
                                        },
                                        '&:hover': {
                                          border: `1px solid ${theme.palette.primary.main}`,
                                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                        },
                                        '&.Mui-focused': {
                                          border: `1px solid ${theme.palette.primary.main}`,
                                          boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                        }
                                      }
                                    },
                                    sx: {
                                      '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                          border: 'none'
                                        },
                                        '&:hover fieldset': {
                                          border: 'none'
                                        },
                                        '&.Mui-focused fieldset': {
                                          border: 'none'
                                        }
                                      },
                                      '& .MuiInputLabel-root': {
                                        fontWeight: 500,
                                        transform: 'translate(14px, 1px) scale(1)',
                                        '&.Mui-focused, &.MuiFormLabel-filled': {
                                          transform: 'translate(14px, -17px) scale(0.75)'
                                        }
                                      }
                                    }
                                  }
                                }}
                              />
                            </LocalizationProvider>
                          ) : (
                            <ReadOnlyTextField
                              label="First Purchase Date"
                              value={selectedSupplier.first_purchase_date || ''}
                              fullWidth
                              variant="outlined"
                              disabled
                            />
                          )}
                        </Grid>

                        {/* Products Supplied - Line Items */}
                        <Grid item xs={12}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: theme.palette.text.primary }}>
                              Products Supplied
                            </Typography>
                            {isEditingSupplier ? (
                              <Box>
                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                  <TextField
                                    value={newProductItem}
                                    onChange={(e) => setNewProductItem(e.target.value)}
                                    placeholder="Add product they supply..."
                                    fullWidth
                                    variant="outlined"
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddProductItem();
                                      }
                                    }}
                                    InputProps={{
                                      sx: { 
                                        borderRadius: '20px !important',
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                        background: 'white',
                                        minHeight: 56,
                                        '& input': {
                                          py: 2.5,
                                          px: 3,
                                          fontSize: '0.9rem',
                                          fontWeight: 500,
                                          minHeight: 'auto'
                                        },
                                        '&:hover': {
                                          border: `1px solid ${theme.palette.primary.main}`,
                                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                        },
                                        '&.Mui-focused': {
                                          border: `1px solid ${theme.palette.primary.main}`,
                                          boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                        }
                                      }
                                    }}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        '& fieldset': { border: 'none' },
                                        '&:hover fieldset': { border: 'none' },
                                        '&.Mui-focused fieldset': { border: 'none' }
                                      }
                                    }}
                                  />
                                  <IconButton
                                    onClick={handleAddProductItem}
                                    sx={{
                                      minWidth: 56,
                                      height: 56,
                                      borderRadius: '20px',
                                      background: theme.palette.primary.main,
                                      color: 'white',
                                      '&:hover': {
                                        background: theme.palette.primary.dark
                                      }
                                    }}
                                  >
                                    <AddIcon />
                                  </IconButton>
                                </Box>
                                {productsSuppliedItems.length > 0 && (
                                  <Box sx={{ 
                                    mt: 2,
                                    p: 2,
                                    borderRadius: '12px',
                                    background: alpha(theme.palette.grey[100], 0.5),
                                    border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                                  }}>
                                    {productsSuppliedItems.map((item, index) => (
                                      <Box 
                                        key={index} 
                                        sx={{ 
                                          display: 'flex', 
                                          alignItems: 'center', 
                                          justifyContent: 'space-between',
                                          py: 1.5,
                                          px: 2,
                                          mb: 1,
                                          borderRadius: '8px',
                                          background: 'white',
                                          border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                                          '&:hover': {
                                            background: alpha(theme.palette.grey[50], 0.8),
                                            boxShadow: `0 2px 8px ${alpha(theme.palette.grey[400], 0.2)}`
                                          }
                                        }}
                                      >
                                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                          {item}
                                        </Typography>
                                        <IconButton 
                                          size="small" 
                                          onClick={() => handleRemoveProductItem(index)}
                                          sx={{ 
                                            color: theme.palette.error.main,
                                            '&:hover': {
                                              background: alpha(theme.palette.error.main, 0.1)
                                            }
                                          }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            ) : (
                              <Box sx={{ 
                                p: 2,
                                borderRadius: '12px',
                                background: alpha(theme.palette.grey[100], 0.5),
                                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                              }}>
                                {(() => {
                                  try {
                                    const items = selectedSupplier.products_supplied 
                                      ? (typeof selectedSupplier.products_supplied === 'string' && selectedSupplier.products_supplied.startsWith('[')
                                          ? JSON.parse(selectedSupplier.products_supplied)
                                          : selectedSupplier.products_supplied.split(',').map(item => item.trim()).filter(item => item))
                                      : [];
                                    return items.length > 0 ? (
                                      <Stack spacing={1}>
                                        {items.map((item, index) => (
                                          <Box 
                                            key={index} 
                                            sx={{ 
                                              display: 'flex', 
                                              alignItems: 'center',
                                              py: 1.5,
                                              px: 2,
                                              borderRadius: '8px',
                                              background: 'white',
                                              border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                                              transition: 'all 0.2s ease',
                                              '&:hover': {
                                                background: alpha(theme.palette.primary.main, 0.05),
                                                borderColor: alpha(theme.palette.primary.main, 0.3),
                                                transform: 'translateX(4px)',
                                                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[400], 0.2)}`
                                              }
                                            }}
                                          >
                                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, color: theme.palette.text.primary }}>
                                              {item}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Stack>
                                    ) : (
                                      <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.9rem', fontStyle: 'italic' }}>
                                        No items added
                                      </Typography>
                                    );
                                  } catch (error) {
                                    return (
                                      <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.9rem' }}>
                                        {selectedSupplier.products_supplied || 'No items added'}
                                      </Typography>
                                    );
                                  }
                                })()}
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </SectionPaper>
                  </Grid>
                  
                  {/* Business Terms Section */}
                  <Grid item xs={12}>
                    <SectionPaper>
                      <SectionTitle variant="h6">
                        <AssignmentIcon />
                        Business Terms
                      </SectionTitle>

                      <Grid container spacing={3} sx={{ mt: 5 }}>
                        <Grid item xs={12} sm={4}>
                          {isEditingSupplier ? (
                            <TextField
                              label="Payment Terms"
                              value={editingSupplierData.payment_terms || ''}
                              onChange={(e) => handleEditingSupplierChange('payment_terms', e.target.value)}
                              fullWidth
                              variant="outlined"
                              InputProps={{
                                sx: { 
                                  borderRadius: '20px !important',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: 'white',
                                  minHeight: 56,
                                  '& input': {
                                    py: 2.5,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    minHeight: 'auto'
                                  },
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  },
                                  '&.Mui-focused': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                  }
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    border: 'none'
                                  },
                                  '&:hover fieldset': {
                                    border: 'none'
                                  },
                                  '&.Mui-focused fieldset': {
                                    border: 'none'
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  fontWeight: 500,
                                  transform: 'translate(14px, 1px) scale(1)',
                                  '&.Mui-focused, &.MuiFormLabel-filled': {
                                    transform: 'translate(14px, -17px) scale(0.75)'
                                  }
                                }
                              }}
                            />
                          ) : (
                            <ReadOnlyTextField
                              label="Payment Terms"
                              value={selectedSupplier.payment_terms || ''}
                              fullWidth
                              variant="outlined"
                              disabled
                            />
                          )}
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          {isEditingSupplier ? (
                            <TextField
                              label="Lead Time (Days)"
                              value={editingSupplierData.lead_time_days || ''}
                              onChange={(e) => handleEditingSupplierChange('lead_time_days', e.target.value)}
                              fullWidth
                              variant="outlined"
                              type="number"
                              InputProps={{
                                sx: { 
                                  borderRadius: '20px !important',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: 'white',
                                  minHeight: 56,
                                  '& input': {
                                    py: 2.5,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    minHeight: 'auto'
                                  },
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  },
                                  '&.Mui-focused': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                  }
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    border: 'none'
                                  },
                                  '&:hover fieldset': {
                                    border: 'none'
                                  },
                                  '&.Mui-focused fieldset': {
                                    border: 'none'
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  fontWeight: 500,
                                  transform: 'translate(14px, 1px) scale(1)',
                                  '&.Mui-focused, &.MuiFormLabel-filled': {
                                    transform: 'translate(14px, -17px) scale(0.75)'
                                  }
                                }
                              }}
                            />
                          ) : (
                            <ReadOnlyTextField
                              label="Lead Time"
                              value={selectedSupplier.lead_time_days || ''}
                              fullWidth
                              variant="outlined"
                              disabled
                            />
                          )}
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          {isEditingSupplier ? (
                            <TextField
                              label="Order Requirements"
                              value={editingSupplierData.order_notes || ''}
                              onChange={(e) => handleEditingSupplierChange('order_notes', e.target.value)}
                              fullWidth
                              variant="outlined"
                              InputProps={{
                                sx: { 
                                  borderRadius: '20px !important',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: 'white',
                                  minHeight: 56,
                                  '& input': {
                                    py: 2.5,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    minHeight: 'auto'
                                  },
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  },
                                  '&.Mui-focused': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                  }
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    border: 'none'
                                  },
                                  '&:hover fieldset': {
                                    border: 'none'
                                  },
                                  '&.Mui-focused fieldset': {
                                    border: 'none'
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  fontWeight: 500,
                                  transform: 'translate(14px, 1px) scale(1)',
                                  '&.Mui-focused, &.MuiFormLabel-filled': {
                                    transform: 'translate(14px, -17px) scale(0.75)'
                                  }
                                }
                              }}
                            />
                          ) : (
                            <ReadOnlyTextField
                              label="Order Requirements"
                              value={selectedSupplier.order_notes || ''}
                              fullWidth
                              variant="outlined"
                              disabled
                            />
                          )}
                        </Grid>

                        <Grid item xs={12}>
                          {isEditingSupplier ? (
                            <TextField
                              label="Notes"
                              value={editingSupplierData.notes || ''}
                              onChange={(e) => handleEditingSupplierChange('notes', e.target.value)}
                              fullWidth
                              variant="outlined"
                              multiline
                              rows={3}
                              InputProps={{
                                sx: { 
                                  borderRadius: '20px !important',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                  background: 'white',
                                  '& textarea': {
                                    py: 2.5,
                                    px: 3,
                                    fontSize: '0.9rem',
                                    fontWeight: 500
                                  },
                                  '&:hover': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                  },
                                  '&.Mui-focused': {
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                  }
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    border: 'none'
                                  },
                                  '&:hover fieldset': {
                                    border: 'none'
                                  },
                                  '&.Mui-focused fieldset': {
                                    border: 'none'
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  fontWeight: 500,
                                  transform: 'translate(14px, 1px) scale(1)',
                                  '&.Mui-focused, &.MuiFormLabel-filled': {
                                    transform: 'translate(14px, -17px) scale(0.75)'
                                  }
                                }
                              }}
                            />
                          ) : (
                            <ReadOnlyTextField
                              label="Notes"
                              value={selectedSupplier.notes || ''}
                              fullWidth
                              variant="outlined"
                              disabled
                              multiline
                              rows={3}
                            />
                          )}
                        </Grid>
                      </Grid>
                    </SectionPaper>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              /* Suppliers List View */
              <Box sx={{ mt: 2 }}>
                
                {loading ? (
                  <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
                    <Card sx={{ 
                      borderRadius: 3,
                      mt: 10,
                      mx: 'auto',
                      maxWidth: 600,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                      boxShadow: `0 12px 32px ${alpha('#9C27B0', 0.15)}`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 16px 40px ${alpha('#9C27B0', 0.2)}`
                      }
                    }}>
                      <CardContent sx={{ p: 6, textAlign: 'center' }}>
                        <Box sx={{ mb: 3 }}>
                          <Box sx={{
                            display: 'inline-flex',
                            p: 3,
                            borderRadius: '50%',
                            bgcolor: alpha('#9C27B0', 0.1),
                            mb: 2
                          }}>
                            <AccountBoxIcon sx={{ fontSize: 48, color: '#9C27B0' }} />
                          </Box>
                        </Box>
                        <Typography variant="h6" sx={{
                          fontWeight: 500,
                          color: '#9C27B0',
                          mb: 1,
                          fontSize: '1.1rem'
                        }}>
                          Loading Supplier Management
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: 'text.secondary',
                          fontWeight: 500,
                          maxWidth: 400,
                          mx: 'auto',
                          lineHeight: 1.6,
                          mb: 3
                        }}>
                          Analyzing supplier data and vendor relationships...
                        </Typography>
                        <Box sx={{ 
                          width: '60%', 
                          mx: 'auto',
                          '& .MuiLinearProgress-root': {
                            height: 8,
                            borderRadius: 4,
                            bgcolor: alpha(theme.palette.grey[300], 0.3),
                            '& .MuiLinearProgress-bar': {
                              bgcolor: '#9C27B0',
                              borderRadius: 4
                            }
                          }
                        }}>
                          <LinearProgress />
                        </Box>
                      </CardContent>
                    </Card>
                  </Container>
                ) : getFilteredSuppliers().length === 0 ? (
                  <Card sx={{ 
                    p: 6, 
                    textAlign: 'center',
                    borderRadius: 3,
                    border: '2px dashed',
                    borderColor: 'primary.200',
                    bgcolor: 'primary.50'
                  }}>
                    <AccountBoxIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2, opacity: 0.7 }} />
                    <Typography variant="h6" color="primary.main" fontWeight="500" gutterBottom>
                      {searchTerm || selectedCategory !== 'all' ? 
                        'No suppliers match your search criteria.' : 
                        'No suppliers found yet'}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      {searchTerm || selectedCategory !== 'all' ?
                        'Try adjusting your search or filter criteria.' :
                        'Add your first supplier to get started managing your supply chain.'}
                    </Typography>
                    {searchTerm || selectedCategory !== 'all' ? (
                      <Button 
                        variant="outlined" 
                        color="primary" 
                        onClick={resetFilters}
                        sx={{ 
                          borderRadius: 3,
                          px: 3,
                          py: 1.5,
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          textTransform: 'none',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                          }
                        }}
                      >
                        Clear Filters
                      </Button>
                    ) : (
                      canEdit && (
                        <Button 
                          variant="outlined" 
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenSupplierDialog()}
                          sx={{ 
                          borderRadius: 2,
                          borderColor: 'grey.400',
                          color: 'primary.main',
                          borderWidth: 1,
                          fontWeight: 500,
                          px: 3,
                          py: 1.5,
                          background: 'white',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          '&:hover': {
                            borderColor: 'grey.600',
                            backgroundColor: 'white',
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                          }
                        }}
                      >
                        Add Your First Supplier
                        </Button>
                      )
                    )}
                  </Card>
                ) : (
                  <Paper sx={{ 
                    borderRadius: 3, 
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '@keyframes slideInUp': {
                      '0%': {
                        transform: 'translateY(100%)',
                        opacity: 0,
                      },
                      '100%': {
                        transform: 'translateY(0)',
                        opacity: 1,
                      }
                    },
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    }
                  }}>
                    <TableContainer sx={{ overflow: 'visible' }}>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell 
                              align="left"
                              sx={{ 
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                bgcolor: 'white',
                                borderBottom: `2px solid ${theme.palette.primary.main}`,
                                color: 'primary.main',
                                py: 2,
                                px: 3,
                                width: 250
                              }}
                            >
                              Supplier Name
                            </TableCell>
                            <TableCell 
                              align="center"
                              sx={{ 
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                bgcolor: 'white',
                                borderBottom: `2px solid ${theme.palette.primary.main}`,
                                color: 'primary.main',
                                py: 2,
                                px: 3
                              }}
                            >
                              Category
                            </TableCell>
                            <TableCell 
                              align="center"
                              sx={{ 
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                bgcolor: 'white',
                                borderBottom: `2px solid ${theme.palette.primary.main}`,
                                color: 'primary.main',
                                py: 2,
                                px: 3
                              }}
                            >
                              Contact
                            </TableCell>
                            <TableCell 
                              align="center"
                              sx={{ 
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                bgcolor: 'white',
                                borderBottom: `2px solid ${theme.palette.primary.main}`,
                                color: 'primary.main',
                                py: 2,
                                px: 3
                              }}
                            >
                              Location
                            </TableCell>
                            <TableCell 
                              align="center"
                              sx={{ 
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                bgcolor: 'white',
                                borderBottom: `2px solid ${theme.palette.primary.main}`,
                                color: 'primary.main',
                                py: 2,
                                px: 3
                              }}
                            >
                              Rating
                            </TableCell>
                            <TableCell 
                              align="center"
                              sx={{ 
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                bgcolor: 'white',
                                borderBottom: `2px solid ${theme.palette.primary.main}`,
                                color: 'primary.main',
                                py: 2,
                                px: 3,
                                width: 120
                              }}
                            >
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getFilteredSuppliers()
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((supplier) => (
                                                         <TableRow 
                          key={supplier.id}
                               hover
                          onClick={() => viewSupplierDetails(supplier)}
                          sx={{
                            cursor: 'pointer',
                                 transition: 'all 0.2s ease',
                            '&:hover': {
                                   bgcolor: 'grey.50',
                                   transform: 'translateX(4px)',
                                   transition: 'all 0.2s ease',
                                   boxShadow: 'inset 4px 0 0 black'
                                 },
                                 '& .MuiTableCell-root': {
                                   borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                   py: 2.5,
                                   px: 3
                                 }
                               }}
                            >
                                                             <TableCell>
                                <Box>
                                  <Typography 
                                      variant="body1" 
                                    sx={{ 
                                      fontWeight: 500,
                                        fontSize: '0.95rem',
                                      color: theme.palette.text.primary,
                                      mb: 0.5
                                    }}
                                  >
                                    {supplier.name}
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: 500,
                                      color: theme.palette.text.secondary,
                                        fontSize: '0.8rem'
                                    }}
                                  >
                                      {supplier.primary_industry || 'No industry'}
                                  </Typography>
                                </Box>
                                                             </TableCell>
                              
                               <TableCell align="center">
                                {supplier.supplier_categories ? (
                                    <Chip 
                                      label={supplier.supplier_categories.name} 
                                      size="small" 
                                      color="secondary"
                                      sx={{ 
                                        fontWeight: 500,
                                        borderRadius: 2,
                                        fontSize: '0.75rem'
                                      }}
                                    />
                                 ) : (
                                   <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: theme.palette.text.secondary }}>
                                    Not categorized
                                  </Typography>
                                )}
                               </TableCell>
                               
                               <TableCell align="center">
                                 <Box>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: 500,
                                       fontSize: '0.9rem',
                                       color: theme.palette.text.primary,
                                       mb: 0.5
                                     }}
                                   >
                                     {supplier.contact_name || 'No contact'}
                                </Typography>
                                {supplier.email && (
                                     <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, fontSize: '0.8rem' }}>
                                    {supplier.email}
                                  </Typography>
                                )}
                              </Box>
                               </TableCell>
                              
                               <TableCell align="center">
                                <Typography 
                                   variant="body2" 
                                  sx={{ 
                                    fontWeight: 500,
                                     fontSize: '0.9rem',
                                     color: theme.palette.text.primary
                                  }}
                                >
                                  {[supplier.city, supplier.state, supplier.country].filter(Boolean).join(', ') || 'No location'}
                                </Typography>
                               </TableCell>
                               
                               <TableCell align="center">
                                 <Rating value={supplier.rating || 0} readOnly size="small" />
                               </TableCell>
                               
                               <TableCell align="center">
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                {canEdit && (
                                  <Tooltip title="Edit Supplier">
                                    <IconButton
                                        size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenSupplierDialog(supplier);
                                      }}
                                      sx={{ 
                                          p: 1,
                                          borderRadius: 2,
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': { 
                                          bgcolor: 'primary.dark',
                                          color: 'white',
                                          transform: 'translateY(-2px) scale(1.05)',
                                          boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.35)}`,
                                          border: `1px solid ${theme.palette.primary.main}`
                                        }
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {hasPermission('supply_chain.suppliers.delete', 'delete') && (
                                  <Tooltip title="Delete Supplier">
                                    <IconButton
                                        size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenDeleteDialog(supplier, 'supplier');
                                      }}
                                      sx={{ 
                                          p: 1,
                                          borderRadius: 2,
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                        bgcolor: 'black',
                                        color: 'white',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': { 
                                          bgcolor: 'grey.900',
                                          color: 'white',
                                          transform: 'translateY(-2px) scale(1.05)',
                                          boxShadow: `0 6px 18px ${alpha(theme.palette.grey[900], 0.4)}`,
                                          border: `1px solid ${theme.palette.grey[900]}`
                                        }
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                                         </TableContainer>
                     
                     <Box sx={{ 
                       borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                       background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                       borderRadius: '0 0 12px 12px'
                     }}>
                       <TablePagination
                         rowsPerPageOptions={[5, 10, 25]}
                         component="div"
                         count={getFilteredSuppliers().length}
                         rowsPerPage={rowsPerPage}
                         page={page}
                         onPageChange={handleChangePage}
                         onRowsPerPageChange={handleChangeRowsPerPage}
                         sx={{ 
                           px: 3,
                           py: 2,
                           '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                             fontSize: '0.9rem',
                             fontWeight: 500,
                             color: theme.palette.text.primary
                           },
                           '& .MuiTablePagination-select': {
                             border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                             borderRadius: 2,
                             px: 2,
                             py: 1,
                             fontWeight: 500,
                             fontSize: '0.9rem',
                             background: 'white',
                             '&:hover': {
                               border: `1px solid ${theme.palette.primary.main}`,
                               boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                             }
                           },
                           '& .MuiTablePagination-actions': {
                             '& .MuiIconButton-root': {
                               border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                               borderRadius: 2,
                               margin: '0 2px',
                               background: 'white',
                               color: theme.palette.primary.main,
                               fontWeight: 500,
                               transition: 'all 0.2s ease',
                               '&:hover': {
                                 background: theme.palette.primary.main,
                                 color: 'white',
                                 transform: 'translateY(-1px)',
                                 border: `1px solid ${theme.palette.primary.main}`,
                                 boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                               },
                               '&.Mui-disabled': {
                                 background: alpha(theme.palette.grey[300], 0.5),
                                 color: theme.palette.grey[500],
                                 border: `1px solid ${theme.palette.grey[300]}`,
                                 transform: 'none',
                                 boxShadow: 'none'
                               }
                             }
                           }
                         }}
                       />
                    </Box>
                  </Paper>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Categories Tab */}
        {tabValue === 1 && (
          <Box sx={{ width: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} sx={{ px: 2 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" component="h2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                  Supplier Categories
                </Typography>
                <Tooltip title="Categories help you organize suppliers by type (e.g., Packaging, Ingredients, Marketing)">
                  <InfoIcon color="action" sx={{ ml: 1, fontSize: 20 }} />
                </Tooltip>
              </Box>
              {canEdit && (
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenCategoryDialog()}
                sx={{
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  fontSize: '0.9rem',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px) scale(1.02)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                  }
                }}
                                    >
                      Add Category
                    </Button>
                  )}
            </Box>
            
            {loading ? (
              <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
                <Card sx={{ 
                  borderRadius: 3,
                  mt: 10,
                  mx: 'auto',
                  maxWidth: 600,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.2)}`
                  }
                }}>
                  <CardContent sx={{ p: 6, textAlign: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ 
                        display: 'inline-flex',
                        p: 3,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        mb: 2
                      }}>
                        <AccountBoxIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                      </Box>
                    </Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 500,
                      color: 'primary.main',
                      mb: 1,
                      fontSize: '1.1rem'
                    }}>
                      Loading Supplier Management
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'text.secondary',
                      fontWeight: 500,
                      maxWidth: 400,
                      mx: 'auto',
                      lineHeight: 1.6,
                      mb: 3
                    }}>
                      Analyzing supplier data and vendor relationships...
                    </Typography>
                    <Box sx={{ 
                      width: '60%', 
                      mx: 'auto',
                      '& .MuiLinearProgress-root': {
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(theme.palette.grey[300], 0.3),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: 'primary.main',
                          borderRadius: 4
                        }
                      }
                    }}>
                      <LinearProgress />
                    </Box>
                  </CardContent>
                </Card>
              </Container>
            ) : getFilteredCategories().length === 0 ? (
              <Card sx={{ 
                p: 6, 
                textAlign: 'center',
                borderRadius: 3,
                border: '2px dashed',
                borderColor: 'secondary.200',
                bgcolor: 'secondary.50'
              }}>
                <FolderIcon sx={{ fontSize: 60, color: 'primary.main', opacity: 0.7, mb: 2 }} />
                <Typography variant="h6" color="primary.main" fontWeight="500" gutterBottom>
                  {categories.length === 0 ? 'No Categories Yet' : 'No Categories Found'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  {categories.length === 0 
                    ? 'Create categories to organize your suppliers by type, such as packaging vendors, ingredient suppliers, or marketing agencies.'
                    : 'No categories match your current search criteria. Try adjusting your search terms.'
                  }
                </Typography>
                {categories.length === 0 ? (
                  canEdit && (
                    <Button 
                      variant="contained" 
                      color="primary" 
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenCategoryDialog()}
                      sx={{ 
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      textTransform: 'none',
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                      transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px) scale(1.02)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                      }
                    }}
                  >
                    Create First Category
                    </Button>
                  )
                ) : (
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    onClick={resetFilters}
                    sx={{ 
                      borderRadius: 3,
                      px: 3,
                      py: 1.5,
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      textTransform: 'none',
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                      }
                    }}
                  >
                    Clear Search
                  </Button>
                )}
              </Card>
            ) : (
              <Grid container spacing={3} sx={{ 
                mt: 0,
                animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '@keyframes slideInUp': {
                  '0%': {
                    transform: 'translateY(100%)',
                    opacity: 0,
                  },
                  '100%': {
                    transform: 'translateY(0)',
                    opacity: 1,
                  }
                }
              }}>
                {getFilteredCategories().map((category) => {
                  // Count suppliers in this category
                  const supplierCount = suppliers.filter(s => s.category_id === category.id).length;
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={category.id}>
                      <Card sx={{ 
                        p: 3, 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        boxShadow: `0 4px 16px ${alpha(theme.palette.grey[900], 0.1)}`,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 12px 32px ${alpha(theme.palette.grey[900], 0.25)}`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                        }
                      }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Typography variant="h6" component="h3" gutterBottom>
                            {category.name}
                          </Typography>
                          <Box>
                            {canEdit && (
                              <IconButton 
                                size="small" 
                                onClick={() => handleOpenCategoryDialog(category)}
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                            {hasPermission('supply_chain.suppliers.delete', 'delete') && (
                              <Tooltip title={supplierCount > 0 ? "Cannot delete category with suppliers assigned" : "Delete category"}>
                                <span>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleOpenDeleteDialog(category, 'category')}
                                    color="error"
                                    disabled={supplierCount > 0}
                                  sx={{
                                    '&:hover': {
                                      transform: 'scale(1.1)',
                                      transition: 'transform 0.2s'
                                    },
                                    '&.Mui-disabled': {
                                      opacity: 0.4
                                    }
                                  }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                        
                        <Chip 
                          label={`${supplierCount} supplier${supplierCount === 1 ? '' : 's'}`} 
                          size="small" 
                          color={supplierCount > 0 ? "primary" : "default"}
                          sx={{ mb: 2, alignSelf: 'flex-start' }}
                        />
                        
                        {category.description && (
                          <Typography variant="body2" color="textSecondary" paragraph sx={{ flexGrow: 1 }}>
                            {category.description}
                          </Typography>
                        )}
                        
                        <Box display="flex" justifyContent="space-between" mt={2} gap={1}>
                          <Button 
                            size="small" 
                            variant="outlined"
                            onClick={() => {
                              setTabValue(0);
                              setSelectedCategory(category.id);
                            }}
                            startIcon={<FilterListIcon />}
                            sx={{ flexGrow: 1 }}
                          >
                            Filter
                          </Button>
                          
                          <Button 
                            size="small" 
                            variant="outlined"
                            onClick={handleOpenSupplierSelectionDialog}
                            startIcon={<SearchIcon />}
                            sx={{ flexGrow: 1 }}
                          >
                            Find
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}

        {/* Supplier Dialog */}
        <Dialog 
          open={openSupplierDialog} 
          onClose={handleCloseSupplierDialog}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: 4,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            fontSize: '1.25rem', 
            fontWeight: 500,
            color: 'text.primary',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
          }}>
            {currentSupplier ? 'Edit Supplier' : 'Add New Supplier'}
          </DialogTitle>
          
          {/* Stepper */}
          <Box sx={{ px: 3, pt: 3 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <DialogContent dividers sx={{ p: 3 }}>
            <Stack spacing={4}>
              {/* Step 0: Basic Information Section */}
              {activeStep === 0 && (
              <Card sx={{ p: 3, borderRadius: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
                <Typography variant="h6" color="primary.main" fontWeight="500" gutterBottom sx={{ mb: 3, fontSize: '1.1rem' }}>
                  Basic Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Supplier Name *"
                      name="name"
                      value={supplierForm.name}
                      onChange={handleSupplierChange}
                      required
                      autoFocus
                      InputProps={{
                        sx: { 
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& input': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            minHeight: 'auto'
                          },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '20px !important',
                          '& fieldset': {
                            border: 'none',
                            borderRadius: '20px !important'
                          },
                          '&:hover fieldset': {
                            border: 'none',
                            borderRadius: '20px !important'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none',
                            borderRadius: '20px !important'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          transform: 'translate(14px, 1px) scale(1)',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(14px, -17px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Category"
                      value={getSelectedCategoryName() || ''}
                      onClick={handleOpenCategorySelectionDialog}
                      placeholder="Select Category"
                      helperText="Type of supplier (e.g., Packaging, Ingredients, Marketing)"
                      InputProps={{
                        readOnly: true,
                        sx: { 
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          cursor: 'pointer',
                          '& input': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            minHeight: 'auto',
                            cursor: 'pointer'
                          },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '20px !important',
                          '& fieldset': {
                            border: 'none',
                            borderRadius: '20px !important'
                          },
                          '&:hover fieldset': {
                            border: 'none',
                            borderRadius: '20px !important'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none',
                            borderRadius: '20px !important'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          transform: 'translate(14px, 1px) scale(1)',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(14px, -17px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Primary Industry"
                      name="primary_industry"
                      value={supplierForm.primary_industry}
                      onChange={handleSupplierChange}
                      placeholder="Main industry the supplier serves"
                      helperText="e.g., Packaging, Ingredients, Marketing"
                      InputProps={{
                        sx: { 
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& input': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            minHeight: 'auto'
                          },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '20px !important',
                          '& fieldset': {
                            border: 'none',
                            borderRadius: '20px !important'
                          },
                          '&:hover fieldset': {
                            border: 'none',
                            borderRadius: '20px !important'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none',
                            borderRadius: '20px !important'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          transform: 'translate(14px, 1px) scale(1)',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(14px, -17px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography component="legend" variant="subtitle2" color="text.secondary" gutterBottom>
                        Quality Rating
                      </Typography>
                      <Rating
                        name="rating"
                        value={supplierForm.rating}
                        onChange={handleRatingChange}
                        size="large"
                      />
                      <FormHelperText>
                        Rate your experience with this supplier (1-5 stars)
                      </FormHelperText>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
              )}
              
              {/* Step 1: Contact Information Section */}
              {activeStep === 1 && (
              <Card sx={{ p: 3, borderRadius: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
                <Typography variant="h6" color="primary.main" fontWeight="500" gutterBottom sx={{ mb: 3 }}>
                  Contact Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      name="contact_name"
                      value={supplierForm.contact_name}
                      onChange={handleSupplierChange}
                      helperText="Primary contact person at this supplier"
                      InputProps={{
                        sx: { 
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& input': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            minHeight: 'auto'
                          },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          transform: 'translate(14px, 1px) scale(1)',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(14px, -17px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={supplierForm.email}
                      onChange={handleSupplierChange}
                      helperText="Contact email address"
                      InputProps={{
                        sx: { 
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& input': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            minHeight: 'auto'
                          },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          transform: 'translate(14px, 1px) scale(1)',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(14px, -17px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      name="phone"
                      value={supplierForm.phone}
                      onChange={handleSupplierChange}
                      helperText="Contact phone number"
                      InputProps={{
                        sx: { 
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& input': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            minHeight: 'auto'
                          },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          transform: 'translate(14px, 1px) scale(1)',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(14px, -17px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Website"
                      name="website"
                      value={supplierForm.website}
                      onChange={handleSupplierChange}
                      placeholder="https://example.com"
                      helperText="Company website URL"
                      InputProps={{
                        sx: { 
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& input': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            minHeight: 'auto'
                          },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          transform: 'translate(14px, 1px) scale(1)',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(14px, -17px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Card>
              )}
              
              {/* Step 2: Address Section */}
              {activeStep === 2 && (
              <Card sx={{ p: 3, borderRadius: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
                <Typography variant="h6" color="primary.main" fontWeight="500" gutterBottom sx={{ mb: 3 }}>
                  Address
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      name="address"
                      value={supplierForm.address}
                      onChange={handleSupplierChange}
                      helperText="Company street address"
                      InputProps={{
                        sx: { 
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& input': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            minHeight: 'auto'
                          },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          transform: 'translate(14px, 1px) scale(1)',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(14px, -17px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="City"
                      name="city"
                      value={supplierForm.city}
                      onChange={handleSupplierChange}
                      InputProps={{
                        sx: { 
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& input': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            minHeight: 'auto'
                          },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          transform: 'translate(14px, 1px) scale(1)',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(14px, -17px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="State/Province"
                      name="state"
                      value={supplierForm.state}
                      onChange={handleSupplierChange}
                      InputProps={{
                        sx: { 
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& input': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            minHeight: 'auto'
                          },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          transform: 'translate(14px, 1px) scale(1)',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(14px, -17px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="ZIP/Postal Code"
                      name="zip_code"
                      value={supplierForm.zip_code}
                      onChange={handleSupplierChange}
                      InputProps={{
                        sx: { 
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& input': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            minHeight: 'auto'
                          },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          transform: 'translate(14px, 1px) scale(1)',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(14px, -17px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Country"
                      name="country"
                      value={supplierForm.country}
                      onChange={handleSupplierChange}
                      InputProps={{
                        sx: { 
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& input': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            minHeight: 'auto'
                          },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          transform: 'translate(14px, 1px) scale(1)',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(14px, -17px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Card>
              )}

              {/* Step 3: Supplier Information Section */}
              {activeStep === 3 && (
              <Card sx={{ p: 3, borderRadius: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
                <Typography variant="h6" color="primary.main" fontWeight="500" gutterBottom sx={{ mb: 3 }}>
                  Supplier Information
                </Typography>
                <Grid container spacing={3}>
                  {/* Row 1: Supplier Status, Cost */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ 
                        fontWeight: 500,
                        transform: 'translate(14px, 1px) scale(1)',
                        '&.Mui-focused, &.MuiFormLabel-filled': {
                          transform: 'translate(14px, -17px) scale(0.75)'
                        }
                      }}>
                        Supplier Status
                      </InputLabel>
                      <Select
                        name="supplier_status"
                        value={supplierForm.supplier_status}
                        onChange={handleSupplierChange}
                        sx={{
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& .MuiSelect-select': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500
                          },
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          }
                        }}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="Approved">Approved</MenuItem>
                        <MenuItem value="Provisional">Provisional</MenuItem>
                      </Select>
                      <FormHelperText>Approval status of the supplier</FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ 
                        fontWeight: 500,
                        transform: 'translate(14px, 1px) scale(1)',
                        '&.Mui-focused, &.MuiFormLabel-filled': {
                          transform: 'translate(14px, -17px) scale(0.75)'
                        }
                      }}>
                        Cost
                      </InputLabel>
                      <Select
                        name="cost"
                        value={supplierForm.cost}
                        onChange={handleSupplierChange}
                        sx={{
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& .MuiSelect-select': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500
                          },
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          }
                        }}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="Acceptable Range">Acceptable Range</MenuItem>
                        <MenuItem value="Non Competitive">Non Competitive</MenuItem>
                      </Select>
                      <FormHelperText>Cost competitiveness assessment</FormHelperText>
                    </FormControl>
                  </Grid>
                  {/* Row 2: Cost Range, First Purchase Date */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Cost Range"
                      name="cost_range"
                      value={supplierForm.cost_range}
                      onChange={handleSupplierChange}
                      placeholder="e.g., $100-$500"
                      helperText="Enter the acceptable cost range"
                      InputProps={{
                        sx: { 
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& input': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            minHeight: 'auto'
                          },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          transform: 'translate(14px, 1px) scale(1)',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(14px, -17px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="First Purchase Date"
                        value={supplierForm.first_purchase_date ? dayjs(supplierForm.first_purchase_date) : null}
                        onChange={(newValue) => {
                          handleSupplierChange({
                            target: {
                              name: 'first_purchase_date',
                              value: newValue ? newValue.format('YYYY-MM-DD') : ''
                            }
                          });
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: 'outlined',
                            helperText: 'Date of first purchase from this supplier',
                            InputProps: {
                              sx: { 
                                borderRadius: '20px !important',
                                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                background: 'white',
                                minHeight: 56,
                                '& input': {
                                  py: 2.5,
                                  px: 3,
                                  fontSize: '0.9rem',
                                  fontWeight: 500,
                                  minHeight: 'auto'
                                },
                                '&:hover': {
                                  border: `1px solid ${theme.palette.primary.main}`,
                                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                },
                                '&.Mui-focused': {
                                  border: `1px solid ${theme.palette.primary.main}`,
                                  boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                                }
                              }
                            },
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  border: 'none'
                                },
                                '&:hover fieldset': {
                                  border: 'none'
                                },
                                '&.Mui-focused fieldset': {
                                  border: 'none'
                                }
                              },
                              '& .MuiInputLabel-root': {
                                fontWeight: 500,
                                transform: 'translate(14px, 1px) scale(1)',
                                '&.Mui-focused, &.MuiFormLabel-filled': {
                                  transform: 'translate(14px, -17px) scale(0.75)'
                                }
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  
                  {/* Products Supplied - Line Items */}
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: theme.palette.text.primary }}>
                        Products Supplied
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                          value={newProductItem}
                          onChange={(e) => setNewProductItem(e.target.value)}
                          placeholder="Add product they supply..."
                          fullWidth
                          variant="outlined"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddProductItem();
                            }
                          }}
                          InputProps={{
                            sx: { 
                              borderRadius: '20px !important',
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                              background: 'white',
                              minHeight: 56,
                              '& input': {
                                py: 2.5,
                                px: 3,
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                minHeight: 'auto'
                              },
                              '&:hover': {
                                border: `1px solid ${theme.palette.primary.main}`,
                                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                              },
                              '&.Mui-focused': {
                                border: `1px solid ${theme.palette.primary.main}`,
                                boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                              }
                            }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { border: 'none' },
                              '&:hover fieldset': { border: 'none' },
                              '&.Mui-focused fieldset': { border: 'none' }
                            }
                          }}
                        />
                        <IconButton
                          onClick={handleAddProductItem}
                          sx={{
                            minWidth: 56,
                            height: 56,
                            borderRadius: '20px',
                            background: theme.palette.primary.main,
                            color: 'white',
                            '&:hover': {
                              background: theme.palette.primary.dark
                            }
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                      {productsSuppliedItems.length > 0 && (
                        <Box sx={{ 
                          p: 2,
                          borderRadius: '12px',
                          background: alpha(theme.palette.grey[100], 0.5),
                          border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                        }}>
                          {productsSuppliedItems.map((item, index) => (
                            <Box 
                              key={index} 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                py: 1.5,
                                px: 2,
                                mb: 1,
                                borderRadius: '8px',
                                background: 'white',
                                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                                '&:hover': {
                                  background: alpha(theme.palette.grey[50], 0.8),
                                  boxShadow: `0 2px 8px ${alpha(theme.palette.grey[400], 0.2)}`
                                }
                              }}
                            >
                              <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                {item}
                              </Typography>
                              <IconButton 
                                size="small" 
                                onClick={() => handleRemoveProductItem(index)}
                                sx={{ 
                                  color: theme.palette.error.main,
                                  '&:hover': {
                                    background: alpha(theme.palette.error.main, 0.1)
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      )}
                      <FormHelperText sx={{ mt: 1 }}>Specific products provided by this supplier</FormHelperText>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
              )}

              {/* Step 4: Business Terms Section */}
              {activeStep === 4 && (
              <Card sx={{ p: 3, borderRadius: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
                <Typography variant="h6" color="primary.main" fontWeight="500" gutterBottom sx={{ mb: 3 }}>
                  Business Terms
                </Typography>
                <Grid container spacing={3} sx={{ mt: 5 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Payment Terms"
                      name="payment_terms"
                      value={supplierForm.payment_terms}
                      onChange={handleSupplierChange}
                      placeholder="e.g., Net 30, COD, etc."
                      helperText="Payment terms and conditions"
                      InputProps={{
                        sx: { 
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& input': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            minHeight: 'auto'
                          },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          transform: 'translate(14px, 1px) scale(1)',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(14px, -17px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Lead Time (Days)"
                      name="lead_time_days"
                      type="number"
                      value={supplierForm.lead_time_days}
                      onChange={handleSupplierChange}
                      placeholder="e.g., 7"
                      helperText="Average lead time in days"
                      InputProps={{
                        sx: { 
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& input': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            minHeight: 'auto'
                          },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          transform: 'translate(14px, 1px) scale(1)',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(14px, -17px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Order Requirements"
                      name="order_notes"
                      value={supplierForm.order_notes}
                      onChange={handleSupplierChange}
                      placeholder="e.g., Minimum order quantity, special requirements"
                      helperText="Special ordering requirements or notes"
                      InputProps={{
                        sx: { 
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& input': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            minHeight: 'auto'
                          },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          transform: 'translate(14px, 1px) scale(1)',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(14px, -17px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Notes"
                      name="notes"
                      value={supplierForm.notes}
                      onChange={handleSupplierChange}
                      placeholder="Additional notes about this supplier"
                      helperText="General notes and comments"
                      multiline
                      rows={3}
                      InputProps={{
                        sx: { 
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          minHeight: 56,
                          '& textarea': {
                            py: 2.5,
                            px: 3,
                            fontSize: '0.9rem',
                            fontWeight: 500
                          },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover fieldset': {
                            border: 'none'
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          transform: 'translate(14px, 1px) scale(1)',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            transform: 'translate(14px, -17px) scale(0.75)'
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Card>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              onClick={handleCloseSupplierDialog}
              sx={{ 
                borderRadius: 6,
                textTransform: 'none',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}
            >
              Cancel
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={activeStep === 0}
                sx={{ 
                  borderRadius: 6,
                  textTransform: 'none',
                  fontWeight: 500,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                  }
                }}
              >
                Back
              </Button>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSupplierSubmit}
                  startIcon={<AddIcon />}
                  sx={{ 
                    borderRadius: 6,
                    textTransform: 'none',
                    fontWeight: 500,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-1px) scale(1.02)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                    }
                  }}
                >
                  {currentSupplier ? 'Save Changes' : 'Add Supplier'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  sx={{ 
                    borderRadius: 6,
                    textTransform: 'none',
                    fontWeight: 500,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-1px) scale(1.02)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                    }
                  }}
                >
                  Next
                </Button>
              )}
            </Box>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={handleCloseDeleteDialog}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }
          }}
          PaperProps={{
            sx: {
              borderRadius: 4,
              background: 'white',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle id="delete-dialog-title" sx={{ pb: 2, fontSize: '1.25rem', fontWeight: 500, color: 'text.primary', border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
            Confirm Deletion
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Typography variant="body1" color="text.primary" gutterBottom>
              Are you sure you want to delete this {deleteType}?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              variant="outlined"
              onClick={handleCloseDeleteDialog}
              sx={{ 
                borderRadius: 6,
                textTransform: 'none',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteItem}
              startIcon={<DeleteIcon />}
              sx={{ 
                borderRadius: 6,
                textTransform: 'none',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 6px 18px ${alpha(theme.palette.error.main, 0.25)}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px) scale(1.02)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.error.main, 0.35)}`
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Category Dialog */}
        <Dialog
          open={openCategoryDialog}
          onClose={handleCloseCategoryDialog}
          aria-labelledby="category-dialog-title"
          aria-describedby="category-dialog-description"
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }
          }}
          PaperProps={{
            sx: {
              borderRadius: 4,
              background: 'white',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
              minWidth: 400
            }
          }}
        >
          <DialogTitle id="category-dialog-title" sx={{ pb: 2, fontSize: '1.25rem', fontWeight: 500, color: 'text.primary' }}>
            {currentCategory ? 'Edit Category' : 'Add New Category'}
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Category Name"
                name="name"
                value={categoryForm.name}
                onChange={handleCategoryChange}
                required
                autoFocus
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.grey[400], 0.8)
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 500
                  }
                }}
              />
              <TextField
                fullWidth
                label="Description (Optional)"
                name="description"
                value={categoryForm.description}
                onChange={handleCategoryChange}
                variant="outlined"
                multiline
                rows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.grey[400], 0.8)
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 500
                  }
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              variant="outlined"
              onClick={handleCloseCategoryDialog}
              sx={{ 
                borderRadius: 6,
                textTransform: 'none',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCategorySubmit}
              startIcon={<AddIcon />}
              sx={{ 
                borderRadius: 6,
                textTransform: 'none',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px) scale(1.02)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                }
              }}
            >
              {currentCategory ? 'Save Changes' : 'Add Category'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Supplier Selection Dialog */}
        <Dialog
          open={supplierSelectionDialogOpen}
          onClose={handleCloseSupplierSelectionDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 4,
              height: '70vh',
              width: '400px',
              maxWidth: '400px',
              margin: 'auto',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            fontSize: '1.25rem', 
            fontWeight: 500,
            color: 'text.primary',
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
              Find Supplier
            </Typography>
            <IconButton onClick={handleCloseSupplierSelectionDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
              <TextField
                fullWidth
                placeholder="Search suppliers..."
                value={supplierSearchTerm}
                onChange={(e) => setSupplierSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                }}
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  }
                }}
              />
            </Box>
            <Box sx={{ height: '300px', overflow: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      color: 'primary.main',
                      py: 2,
                      pl: 3,
                      pr: 3
                    }}>
                      Supplier
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      color: 'primary.main',
                      py: 2,
                      pl: 3,
                      pr: 3
                    }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredSuppliersForSelection().map((supplier) => (
                    <TableRow 
                      key={supplier.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'grey.50',
                          transform: 'translateX(4px)',
                          boxShadow: 'inset 4px 0 0 black'
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                          py: 2,
                          pl: 3,
                          pr: 3
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box 
                            sx={{ 
                              p: 1.5, 
                              borderRadius: 3, 
                              bgcolor: 'primary.main',
                              color: 'white',
                              width: 36,
                              height: 36,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                              flexShrink: 0
                            }}
                          >
                            <AccountBoxIcon sx={{ fontSize: 18 }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                color: theme.palette.text.primary,
                                mb: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {supplier.name}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 400,
                                color: theme.palette.text.secondary,
                                fontSize: '0.8rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {supplier.supplier_categories?.name || 'No category'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleSupplierSelect(supplier)}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 1,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                            }
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {getFilteredSuppliersForSelection().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {supplierSearchTerm ? 'No suppliers found matching your search.' : 'No suppliers available.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            p: 3,
            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            background: alpha(theme.palette.grey[50], 0.8)
          }}>
            <Button
              onClick={() => {
                handleCloseSupplierSelectionDialog();
                handleOpenSupplierDialog();
              }}
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ 
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}
            >
              Add New Supplier
                         </Button>
           </DialogActions>
         </Dialog>

        {/* Category Selection Dialog */}
        <Dialog
          open={categorySelectionDialogOpen}
          onClose={handleCloseCategorySelectionDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 4,
              height: '70vh',
              width: '400px',
              maxWidth: '400px',
              margin: 'auto',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
              display: 'flex',
              flexDirection: 'column'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            fontSize: '1.25rem', 
            fontWeight: 500,
            color: 'text.primary',
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
              Select Category
            </Typography>
            <IconButton onClick={handleCloseCategorySelectionDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`, flexShrink: 0 }}>
              <TextField
                fullWidth
                placeholder="Search categories..."
                value={categorySearchTerm}
                onChange={(e) => setCategorySearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                }}
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  }
                }}
              />
            </Box>
            <Box sx={{ 
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              '-ms-overflow-style': 'none',
              'scrollbar-width': 'none'
            }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      color: 'primary.main',
                      py: 2,
                      pl: 3,
                      pr: 3
                    }}>
                      Category
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      color: 'primary.main',
                      py: 2,
                      pl: 3,
                      pr: 3
                    }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* None option */}
                  <TableRow 
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 2,
                        pl: 3,
                        pr: 3
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box 
                          sx={{ 
                            p: 1.5, 
                            borderRadius: 3, 
                            bgcolor: 'grey.400',
                            color: 'white',
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.grey[400], 0.2)}`,
                            flexShrink: 0
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              fontSize: '0.9rem',
                              color: theme.palette.text.secondary,
                              mb: 0.5
                            }}
                          >
                            No Category
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 400,
                              color: theme.palette.text.secondary,
                              fontSize: '0.8rem'
                            }}
                          >
                            Uncategorized supplier
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setSupplierForm({ ...supplierForm, category_id: '' });
                          setCategorySelectionDialogOpen(false);
                        }}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2,
                          py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                          }
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                  
                  {getFilteredCategoriesForSelection().map((category) => (
                    <TableRow 
                      key={category.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'grey.50',
                          transform: 'translateX(4px)',
                          boxShadow: 'inset 4px 0 0 black'
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                          py: 2,
                          pl: 3,
                          pr: 3
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box 
                            sx={{ 
                              p: 1.5, 
                              borderRadius: 3, 
                              bgcolor: 'primary.main',
                              color: 'white',
                              width: 36,
                              height: 36,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                              flexShrink: 0
                            }}
                          >
                            <FolderIcon sx={{ fontSize: 18 }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                color: theme.palette.text.primary,
                                mb: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {category.name}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 400,
                                color: theme.palette.text.secondary,
                                fontSize: '0.8rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {category.description || 'No description'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleCategorySelect(category)}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 1,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                            }
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {getFilteredCategoriesForSelection().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {categorySearchTerm ? 'No categories found matching your search.' : 'No categories available.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            p: 3,
            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            background: alpha(theme.palette.grey[50], 0.8),
            flexShrink: 0
          }}>
            <Button
              onClick={() => {
                handleCloseCategorySelectionDialog();
                handleOpenCategoryDialog();
              }}
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ 
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}
            >
              Add New Category
            </Button>
          </DialogActions>
        </Dialog>

        {/* Category Filter Dialog */}
        <Dialog
          open={categoryFilterDialogOpen}
          onClose={() => {
            setCategoryFilterDialogOpen(false);
            setCategoryFilterSearchTerm('');
          }}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: 'none',
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
              backdropFilter: 'blur(20px)',
              boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{ 
            p: 4, 
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
            color: theme.palette.text.primary,
            fontWeight: 500
          }}>
            Select Category Filter
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            <Box sx={{ mb: 4, mt: 3 }}>
              <TextField
                fullWidth
                placeholder="Search categories..."
                value={categoryFilterSearchTerm}
                onChange={(e) => setCategoryFilterSearchTerm(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Box>
            
            <Box sx={{ 
              maxHeight: 400, 
              overflowY: 'auto',
              border: `2px solid ${theme.palette.grey[400]}`,
              borderRadius: 2,
              background: 'white',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              scrollbarWidth: 'none'
            }}>
              {/* All Categories option */}
              <Box
                onClick={() => handleCategoryFilterSelection('all')}
                sx={{
                  p: 3,
                  borderBottom: `1px solid ${alpha(theme.palette.grey[200], 0.8)}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: selectedCategory === 'all' ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    transform: 'translateX(4px)',
                    boxShadow: 'inset 4px 0 0 black'
                  }
                }}
              >
                <Typography variant="subtitle1">
                  All Categories
                </Typography>
              </Box>
              
              {getFilteredCategoriesForFilter().map(category => (
                <Box
                  key={category.id}
                  onClick={() => handleCategoryFilterSelection(category.id)}
                  sx={{
                    p: 3,
                    borderBottom: `1px solid ${alpha(theme.palette.grey[200], 0.8)}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: selectedCategory === category.id ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      transform: 'translateX(4px)',
                      boxShadow: 'inset 4px 0 0 black'
                    },
                    '&:last-child': {
                      borderBottom: 'none'
                    }
                  }}
                >
                  <Typography variant="subtitle1">
                    {category.name}
                  </Typography>
                </Box>
              ))}
              
              {getFilteredCategoriesForFilter().length === 0 && categoryFilterSearchTerm && (
                <Box sx={{ p: 6, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No categories found matching your search.
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            p: 4, 
            gap: 2, 
            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
          }}>
            <Button 
              onClick={() => {
                setCategoryFilterDialogOpen(false);
                setCategoryFilterSearchTerm('');
              }}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                color: theme.palette.text.secondary,
                fontWeight: 500,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                }
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Fade>
  );
};

export default SuppliersPage;